// Kael T1 모델(Tripo → Mixamo GLB, public/assets/characters/kael_t1.glb).
// 툰 2단 셰이딩 + 상시 림 + 스키닝된 반전 헐 외곽선(ART-BIBLE 3장), 오른손 대검(에셋 GLB, 실패 시 절차 대검), 게임 시간(영웅 시계)으로 도는 애니메이션.
// 로드할 때 공격 클립의 오른손 속도 최대 프레임을 '타격 프레임'으로 찾아, 선딜 끝(판정 시작) 틱에 그 프레임이 오도록 재생 구간과 배율을 정한다.
import * as THREE from 'three/webgpu';
import { KaelTierCache, SWORD_TIER_LOOK, rebindTierGeometry, type KaelTierState } from './kaelTier';
import { measureTierGround, type TierGround } from './kaelTierGround';
import type { GearTier } from '../shared/rpg';
import { maskKaelOutline } from './kaelOutline';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { float, floor, materialColor, materialEmissive, max, mix, normalLocal, normalView, positionLocal, positionViewDirection, screenCoordinate, smoothstep, step, texture, uniform, uv, vec3, vec4, vertexColor } from 'three/tsl';
import { AWAKEN, COMBO, DODGE, HERO, PALETTE, SKILLS, SKILL_TUNING } from '../shared/config';
import { AwakenPhase, HeroAct, Scene, type GameView } from '../shared/types';
import type { GameClocks } from './clock';
import { colored, merge } from './geo';
import type { RimControl } from './materials';
import { createKaelSurface, KAEL_OUTLINE_M, KAEL_SWORD_OUTLINE_M } from './kaelSurface';
import { clamp01, phaseOf } from './util';
import { balanceHalfCycles, DistanceGait } from './locomotion';
import { GroundContact, IDLE_SETTLE, IDLE_SETTLE_TIME, STEP_LIFT as TURN_STEP_LIFT, STEP_MAX, STEP_TIME as TURN_STEP_TIME } from './groundContact';
import { PoseTransition } from './poseTransition';
import { createGroundedDodgeClip } from './dodgeClip';
import { AttackRecoveryGate, createAttackRecoveryClip } from './attackClip';
import { LYRA_BOW_SEGMENTS, lyraBasicPose, lyraSkillPose, type LyraPoseTiming } from './lyraPose';
import { assetUrl } from '../pwa/assetMap';
import { PoseVertexReader } from './poseVertexReader';
import { buildAttackCurve, COIL, curveTime, emptyCurve, SMEAR_STRETCH, smearWeight, SWEEP_TICKS, type ActionCurve } from './actionTiming';

export const KAEL_URL = assetUrl('characters/kael_t1.glb');
/**
 * 라이라 전용 몸(Tripo → Mixamo 리깅·활 클립 → bake_character.py). 카엘 GLB와 같은 구조(Mixamo 이름 골격, 같은 클립 이름)에
 * 활 전용 클립(bow_draw·bow_shot·bow_sky·bow_back·bow_air)을 더했다. KaelModel이 그대로 읽는다(단계 교체 없음).
 */
export const LYRA_URL = assetUrl('characters/lyra_t1.glb');
/**
 * 카엘 천공 올려베기(스킬 5) 전용 클립: Mixamo "Standing Melee Attack Backhand"(아래→위 역수 올려치기)를 카엘 리그로 리타깃한
 * 골격·클립만 든 GLB(hero_bake.py --anim-only, 0.6~1.7초 구간, 제자리). 없으면 초승달 휘두름 구간으로 대신한다.
 */
export const KAEL_LAUNCHER_URL = assetUrl('characters/kael_launcher.glb');
/** 올려베기 클립 구간(초): 오른손이 허리 아래(0.25s)에서 가슴 높이를 지나(판정 0.47s) 머리 위 최고점(0.63s) 뒤 거둠까지 */
export const KAEL_LAUNCHER_SEG = { clip: 'launcher', s0: 0.25, hit: 0.47, s1: 1.1 } as const;

/** 골격·클립만 든 GLB에서 클립을 읽는다(실패하면 빈 목록 — 대체 클립으로 남는다) */
export async function loadExtraClips(url: string): Promise<THREE.AnimationClip[]> {
  try {
    const g = await new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).loadAsync(url);
    return g.animations;
  } catch (err) {
    console.warn('추가 클립을 읽지 못해 대체 클립으로 둔다', url, err);
    return [];
  }
}
/** 고정 카메라에서 읽히는 크기 배율(판정 반경은 바꾸지 않는다). 원본 신장 1.75m → 약 2.6m(고블린의 약 1.9배) */
export const KAEL_SCALE = 1.5;
/** 연타 사이 섞는 시간(초). 가장 짧은 선딜 70ms 안에서 끝난다 */
const CHAIN_FADE = 0.05;
/** 공격이 끝나고 대기 자세로 돌아가는 섞는 시간(초): 클립의 빠른 가드 복귀 대신 이 시간 동안 부드럽게 돌아온다 */
const RECOVER_FADE = 0.16;
/**
 * 대기↔달리기 상체·검 휴대 전환 시간(영웅 초). 다리는 기존 섞기로 바로 반응하고, 골반·척추·목·팔·손과 검 소켓만 이 시간 동안
 * 연속 회전 호로 잇는다. 2026-10-19 측정: 0.1초 섞기에서 칼끝이 14~18m/s(정상 달리기 최대 4.9m/s의 3.7배)로 가드→휴대 자세를 홱 돌았다.
 * 걷기(스틱 절반)는 원본 칼끝 속도가 낮아 더 길게 잇는다: 6m/s 이상 0.55초, 4m/s 이하 0.6초, 사이는 선형.
 */
const CARRY_FADE_RUN = 0.55;
const CARRY_FADE_WALK = 0.6;
/** 회전 디딤을 켜기 시작하는 몸-이동 방향 차이(rad)와 최대가 되는 차이 */
const TURN_PLANT_ON = (15 * Math.PI) / 180;
const TURN_PLANT_FULL = (35 * Math.PI) / 180;
/** 대기 정리 걸음을 허용하는 대기 경과(영웅 초): 멈출 때 다리 섞기(STOP_FADE)와 골반·상체 전환(CARRY_FADE_WALK)이 끝난 뒤 */
const IDLE_SETTLE_AFTER = 0.6;
/** 대기(멈춤)에서 다리 길이 한계로 옮겨 딛는 시간·높이: 0.1초·0.15m 걸음은 엉덩이·무릎 각속도가 달리기 최대의 1.5배였다 */
const IDLE_STEP_TIME = 0.22;
const IDLE_STEP_LIFT = 0.13;
/**
 * 회전 디딤을 켜는 달리기 경과(영웅 초). 멈춰 있다가 크게 돌며 달리기 시작할 때는 몸이 수천 °/s로 돌아
 * 디딘 발을 붙이면 엉덩이 관절이 1,192°/s(대기·달리기 최대 808°/s의 1.48배)로 비틀렸다(2026-10-20 벽 서쪽 30Hz).
 */
const TURN_PLANT_AFTER_RUN = 0.25;
/** 달리기→정지 다리 섞기(초). 0.12초에서는 벌린 보폭의 발이 대기 자세로 15m/s(달리기 최대의 1.5배)로 끌려왔다. */
const STOP_FADE = 0.25;
/** 2타 팔 회복 표시 클립(후보18)의 팔을 이 틱 자세로 끝까지 유지한다. 2026-10-19 측정: t15.5~17에서 오른 팔꿈치가 -95~-99도로 거꾸로 꺾였다(원본 같은 시각 +43~37도). */
const TAIL_ARM_HOLD_TICK = 15;
/** 후딜 표시가 칼을 바닥 아래로 꽂지 않게 하는 여유(월드 m). 4타 원본은 후딜에서 칼끝을 바닥 아래 0.6m까지 꽂고 머물렀다. */
const RECOVERY_SWORD_CLEARANCE = 0.06;
/**
 * 붉은 낙화·피의 돌진이 끝나 대기로 돌아가는 섞기(초). 낙화는 착지 판정이 마지막 틱이라 0.1초 섞기에서는 착지 웅크림이 2~3프레임만 보였다.
 * 착지 무게가 읽히게 웅크린 자세에서 천천히 일어선다(판정·이동은 이미 끝나 조작에는 영향 없음).
 */
const LAND_FADE = 0.24;
/** 칼날 폭풍 전용 액션 이름(4타 클립 복제) */
const STORM_CLIP = 'storm';
/** 공격이 끝난 뒤 몸을 게임 위치로 합치는 빠르기(1/영웅 초): 0.16초에 95% */
const RM_RELEASE = 3 / 0.16;
/** 보이는 몸과 게임 위치 사이 최대 거리(m). 벽·무리에 막혀 게임 위치가 서도 몸이 벽을 뚫고 나가지 않게 */
const RM_MAX_OFFSET = 1.0;
/** 외곽선 두께(월드 m). 카메라 거리 19m, 720p에서 약 1.2px */
const OUTLINE_M = KAEL_OUTLINE_M;
/** 칼끝 궤적 샘플 수(SlashFx 리본 정점 쌍 수와 같다) */
export const TRAIL_N = 41;
const TICK = 1 / 60;
/** 스킬 7 내려찍기 판정 틱(shared/skills/kael.ts SPLIT_IMPACT = 300ms) */
const SPLIT_IMPACT_TICK = 18;
/** 스킬 6 난무 구간(130ms~530ms 5타)과 스킬 8 추가 회전 수 */
const AIR_SWING_FROM = 8;
const AIR_SWING_TO = 32;
const SPIN_EXTRA_TURNS = 8;

/** 클립 구간 [s0, hit, s1](초)을 게임 틱 [0, anchor, total]에 맞춘다. */
export interface Segment {
  clip: string;
  s0: number;
  hit: number;
  s1: number;
  anchor: number;
  total: number;
}

export interface AttackTiming extends Segment {
  dur: number;
  /** 오른손 최대 속도(m/s, 원본 크기) */
  peak: number;
  sw0: number;
  sw1: number;
  ratePre: number;
  ratePost: number;
  /** 칼끝·칼날 중간 궤적(모델 기준 로컬 m, 원본 크기), 휘두름 구간 [sw0, sw1] */
  tip: Float32Array;
  base: Float32Array;
  /** 판정 시작 순간 이미 지나간 궤적 비율 */
  headStart: number;
  /** 남은 궤적을 그리는 틱 수 */
  drawTicks: number;
  /** 클립 몸 이동(엉덩이 수평 위치, 루트 로컬 m·배율 1) 60Hz 표본. null이면 몸 이동 없음 */
  rm: Float32Array | null;
  /** 구간 시작(s0)의 몸 이동 값 */
  rm0x: number;
  rm0z: number;
  /** 표시 클립 시각 곡선(actionTiming.ts): 예비동작 머묾 → 판정 틱 최고속 → sweep 틱 안에 휘두름 끝 → 뻗은 자세 감속 */
  curve: ActionCurve;
}

/** 몸 이동 표본(60Hz, x·z 교대)에서 클립 시각 time의 값을 선형 보간해 out에 쓴다 */
export function rmAt(d: Float32Array, time: number, out: { x: number; z: number }): void {
  const n = (d.length >> 1) - 1;
  const f = Math.max(0, Math.min(n, time / TICK));
  const i = Math.min(n - 1, Math.floor(f)), u = f - i;
  out.x = d[i * 2] + (d[i * 2 + 2] - d[i * 2]) * u;
  out.z = d[i * 2 + 1] + (d[i * 2 + 3] - d[i * 2 + 1]) * u;
}

export function segTime(g: Segment, t: number): number {
  if (t <= g.anchor) return g.s0 + (g.hit - g.s0) * (g.anchor > 0 ? clamp01(t / g.anchor) : 1);
  const rest = g.total - g.anchor;
  return g.hit + (g.s1 - g.hit) * (rest > 0 ? clamp01((t - g.anchor) / rest) : 1);
}

/** 카엘 대검(T1). 원점 폼멜 아래 끝, 길이 +Y 1.5m, 손잡이 중심은 아래 끝에서 SWORD_GRIP_M(에셋 인계 qa/assets/m1p-anim/HANDOFF-CLIPS.md) */
export const SWORD_URL = assetUrl('weapons/wpn_kael_greatsword_t1.glb');
const SWORD_GRIP_M = 0.2;
/**
 * 양손 쥐기(2026-11-02 대표님 피드백 "검 휘두르는 동작이 언밸런스"). 대검은 오른손 본에만 붙어 있어 Mixamo 양손 클립의 왼손과 따로 놀았다:
 * CPU 계측(qa/warrior-swing/balance-before.json)에서 왼손 주먹이 손잡이 축을 따라 -0.08~-0.23m(폼멜 끝 -0.2m 밖까지) 미끄러지고,
 * 칼날이 손 안에서 최대 1,008°/s로 비틀렸다. 매 프레임 칼날 축을 '왼손 주먹 → 오른손 손잡이점'으로 조준하고 손잡이 중심을 두 주먹 사이에 둔다.
 * 두 주먹이 TWO_HAND_NEAR(m, 원본 배율) 안이면 완전히 양손, TWO_HAND_FAR 밖이면(한 손 휴대·달리기) 끈다. 조준 보정이
 * TWO_HAND_MAX_DEG를 넘으면(왼손이 칼에서 떨어진 자세) 서서히 끈다. 손잡이 중심 이동은 TWO_HAND_SLIDE_M까지(코등이 0.1m 앞).
 */
const TWO_HAND_NEAR = 0.3;
const TWO_HAND_FAR = 0.5;
const TWO_HAND_MAX_DEG = 80;
const TWO_HAND_SLIDE_M = 0.06;
/** 양손 조준 보정 회전의 최대 각속도(rad/s, 화면 재생만) */
const TWO_HAND_RATE = 9;
/**
 * 날 세우기(같은 피드백): 원본 클립의 손목 비틀림 그대로면 칼날이 휘두르는 중에 최대 1,008°/s로 돌고, 3타 내려베기는 휘두름 방향과 날 폭 축의
 * |cos|가 0.1~0.25로 넓은 면으로 후려쳤다(qa/warrior-swing/balance-before.json). 칼끝이 EDGE_V0(m/s, 몸 기준·원본 배율) 이상 빠르면
 * 칼날 축을 도는 비틀림만 더해 날이 휘두름 방향을 앞장서게 하고(칼끝·궤적 위치는 축 위라 바뀌지 않는다), 느려지면 원본 비틀림으로 천천히 돌아간다.
 */
const EDGE_V0 = 3;
const EDGE_V1 = 8;
const EDGE_FOLLOW = 20;
const EDGE_RELEASE = 6;
const EDGE_MAX = (80 * Math.PI) / 180;
/** 더하는 비틀림의 최대 각속도(rad/s): 휘두름 방향이 꺾이는 순간 날이 홱 돌지 않게 */
const EDGE_RATE = 5;

/** 카엘과 대검을 함께 읽는다. 대검은 gltf.userData.sword(실패하면 null → 절차 대검) */
/** attempt>1(다시 시도)이면 주소에 시도 번호를 붙여 응답 없이 멈춘 이전 요청에 합쳐지지 않고 새로 받는다 */
export function loadKaelGltf(onProgress: (p: number) => void, attempt = 1, url = KAEL_URL): Promise<GLTF> {
  const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
  const q = attempt > 1 ? '?retry=' + attempt : '';
  const kael = new Promise<GLTF>((resolve, reject) => {
    loader.load(url + q, resolve, (e) => { if (e.total > 0) onProgress(e.loaded / e.total); }, reject);
  });
  const sword = new Promise<GLTF | null>((resolve) => loader.load(SWORD_URL + q, resolve, undefined, () => resolve(null)));
  return Promise.all([kael, sword]).then(([g, s]) => { g.userData.sword = s; return g; });
}

/** 대검 GLB → 절차 대검과 같은 축(손잡이 원점, 날 +Z)의 실수형 지오메트리와 베이스컬러 텍스처. 메시가 없으면 null */
function swordFromGltf(s: GLTF | null): { geometry: THREE.BufferGeometry; map: THREE.Texture | null } | null {
  if (!s) return null;
  const parts: THREE.BufferGeometry[] = [];
  let map: THREE.Texture | null = null;
  s.scene.updateMatrixWorld(true);
  s.scene.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    // 양자화 속성(KHR_mesh_quantization)은 정규화 정수라 행렬을 바로 곱하면 넘친다: 실수로 바꾼 뒤 옮긴다
    const g = new THREE.BufferGeometry();
    for (const name of ['position', 'normal', 'uv'] as const) {
      const a = m.geometry.getAttribute(name);
      if (!a) continue;
      const out = new Float32Array(a.count * a.itemSize);
      for (let i = 0; i < a.count; i++) for (let k = 0; k < a.itemSize; k++) out[i * a.itemSize + k] = a.getComponent(i, k);
      g.setAttribute(name, new THREE.BufferAttribute(out, a.itemSize));
    }
    if (m.geometry.index) g.setIndex(Array.from(m.geometry.index.array as ArrayLike<number>));
    g.applyMatrix4(m.matrixWorld);
    parts.push(g);
    const mat = m.material as THREE.MeshStandardMaterial;
    if (!map && mat && mat.map) map = mat.map;
  });
  if (!parts.length) return null;
  const geometry = parts.length === 1 ? parts[0] : mergeGeometries(parts) ?? parts[0];
  // +Y 길이 → +Z(날), 손잡이 중심을 원점으로
  geometry.rotateX(Math.PI / 2).translate(0, 0, -SWORD_GRIP_M);
  if (!geometry.getAttribute('normal')) geometry.computeVertexNormals();
  return { geometry, map };
}

/** settle: 휘두름이 끝난 뒤 칼끝(손) 속도가 가장 낮아지는 시각. 그 뒤는 가드로 되돌리는 동작이다 */
interface Swing { dur: number; hit: number; peak: number; sw0: number; sw1: number; settle: number }

interface TierSurface extends TierGround { geometry: THREE.BufferGeometry; material: THREE.MeshToonNodeMaterial }

export class KaelModel {
  private readonly tierCache: KaelTierCache<TierSurface>;
  private readonly restMeshMatrix: THREE.Matrix4;
  private readonly restRigMatrix: THREE.Matrix4;
  private readonly restJoints = new Map<string, THREE.Vector3>();
  private originalDodgeClip: THREE.AnimationClip | null = null;
  readonly root = new THREE.Group();
  private readonly orient = new THREE.Group();
  /** 단계 교체 때 새 객체로 바뀐다(applyTier) */
  mesh: THREE.SkinnedMesh;
  private hull: THREE.SkinnedMesh;
  /** 가려졌을 때만 보이는 실루엣(x-ray) */
  private xray: THREE.SkinnedMesh;
  private readonly xrayMat: THREE.MeshBasicNodeMaterial;
  private readonly mixer: THREE.AnimationMixer;
  private readonly actions = new Map<string, THREE.AnimationAction>();
  private readonly clips = new Map<string, THREE.AnimationClip>();
  private toon: THREE.MeshToonNodeMaterial;
  private readonly swordMat: THREE.MeshStandardNodeMaterial;
  /** 대검 칼날 단계별 빛(ECONOMY 11절): 새 메시·재질 없이 uniform 값만 바꾼다(WebGPU 파이프라인 재사용) */
  private readonly swordEdge = uniform(new THREE.Vector3(...SWORD_TIER_LOOK[1].edge));
  private readonly swordGlow = uniform(new THREE.Vector3(...SWORD_TIER_LOOK[1].glow));
  private readonly swordBlade = uniform(new THREE.Vector3(...SWORD_TIER_LOOK[1].blade));
  private readonly swordTint = uniform(new THREE.Vector3(...SWORD_TIER_LOOK[1].tint));
  private readonly swordTierF = uniform(1);
  private weaponTierValue: GearTier = 1;
  private readonly hullMat: THREE.MeshBasicNodeMaterial;
  private readonly swordGeo: THREE.BufferGeometry;
  private readonly swordHull: THREE.Mesh;
  /** 대검 본체(스미어 늘이기용: 판정 틱 전후 칼날 길이 방향 배율만 바꾼다) */
  private readonly swordMesh: THREE.Mesh;
  private readonly thickness = uniform(1);
  private readonly bones: Record<'hand' | 'hips' | 'lFoot' | 'rFoot' | 'lToe' | 'lUpLeg' | 'rUpLeg', THREE.Bone>;
  private readonly swordMount = new THREE.Group();
  /** 전투 장착 회전(setSwordMount 값). 달리기에서 바꿨다가 이 값으로 되돌린다 */
  private readonly mountQ = new THREE.Quaternion();
  private readonly mountP = new THREE.Vector3();
  /** 달리기 클립의 쥔 손에서 구한 축·중심. 팔/손은 원본 클립 그대로이며 손잡이만 주먹에 맞춘다. */
  private readonly runMountQ = new THREE.Quaternion();
  private readonly runMountP = new THREE.Vector3();
  private socketCarry = 0;
  private transitionCarry = 0;
  private readonly tipMark = new THREE.Object3D();
  private readonly baseMark = new THREE.Object3D();
  private readonly v = new THREE.Vector3();
  /** 양손 쥐기(applyTwoHandGrip) 프레임 임시값: 프레임 루프 할당 없음 */
  private readonly gripBones: { lA: THREE.Bone; lB: THREE.Bone } | null;
  private readonly gripInv = new THREE.Matrix4();
  private readonly gripL = new THREE.Vector3();
  private readonly gripT = new THREE.Vector3();
  private readonly gripD = new THREE.Vector3();
  private readonly gripB = new THREE.Vector3();
  private readonly gripQ = new THREE.Quaternion();
  private readonly gripBaseQ = new THREE.Quaternion();
  private readonly gripBaseP = new THREE.Vector3();
  private readonly gripIdQ = new THREE.Quaternion();
  private readonly gripAimQ = new THREE.Quaternion();
  private readonly gripPrevQ = new THREE.Quaternion();
  private gripHasPrev = false;
  /** 날 세우기 비틀림(rad, 칼날 축 기준)과 직전 칼끝(루트 기준) */
  private edgeRoll = 0;
  private readonly edgePrev = new THREE.Vector3();
  private edgeHasPrev = false;
  private readonly edgeQ = new THREE.Quaternion();
  private readonly edgeZ = new THREE.Vector3(0, 0, 1);
  /** 마지막 프레임 양손 쥐기 가중치 0..1(QA 읽기) */
  twoHandWeight = 0;
  /** 마지막 프레임 양손 조준 보정 각(도, QA 읽기) */
  twoHandDeg = 0;
  /** QA·비교용: false면 예전 한 손 소켓 그대로(?grip=1h, 전/후 같은 픽스처 비교) */
  twoHandGrip = !(typeof location !== 'undefined' && new URLSearchParams(location.search).get('grip') === '1h');
  private handScale = 0.01;

  readonly attacks: AttackTiming[] = [];
  dash: Segment = { clip: 'dash', s0: 0, hit: 0.5, s1: 1, anchor: 10, total: 25 };
  leap: Segment = { clip: 'leap', s0: 0, hit: 1, s1: 1.2, anchor: 38, total: 39 };
  /** 스킬 4 진홍 초승달: 2타(가로 휘두름) 클립 구간을 선딜 9틱·길이 33틱에 맞춘다(analyze가 채운다) */
  crescent: Segment = { clip: 'atk2', s0: 0, hit: 0.5, s1: 1, anchor: SKILL_TUNING.crescentWindup, total: 33 };
  /** 스킬 4 초승달·스킬 5 올려베기 표시 곡선(기본 공격과 같은 actionTiming 곡선, 2타 휘두름 구간) */
  private readonly crescentCurve = emptyCurve();
  private readonly launcherCurve = emptyCurve();
  dodge: Segment = { clip: 'dodge', s0: 0.05, hit: 0.5, s1: 1.0, anchor: 10, total: DODGE.duration };
  /** 칼날 폭풍: atk4의 회전 구간 [a, b]를 loops회 반복 */
  /** dir: 회전 방향(rotation.y 부호) */
  storm = { clip: 'atk4', a: 0.3, b: 1.2, loops: 2, turnDeg: 0, dir: 1 };
  /** 클립별 몸 이동 표본(extractRootMotion) */
  private readonly rootMotion = new Map<string, Float32Array>();
  /** 공격 중 몸 고정점(부모 공간 m)과 현재 몸 오프셋(부모 공간 m, 보이는 몸 - 게임 위치) */
  private rmLocked = false;
  private rmSerial = -1;
  private rmBX = 0;
  private rmBZ = 0;
  private offX = 0;
  private offZ = 0;
  private readonly rmP = { x: 0, z: 0 };
  powerup = { s0: 0, s1: 2.4 };
  /** 라이라 임시 자세 구간(c022, lyraPose.ts): 프레임마다 값만 채운다(할당 없음) */
  private readonly lyraSeg: Segment = { clip: 'atk2', s0: 0, hit: 0.5, s1: 1, anchor: 6, total: 24 };
  /** 천공 올려베기 전용 클립 구간(addClips로 'launcher'가 들어왔을 때만 쓴다). 판정 틱 = 초승달과 같은 선딜 */
  private readonly launcherSeg: Segment = { ...KAEL_LAUNCHER_SEG, anchor: SKILL_TUNING.crescentWindup, total: SKILLS[4]?.duration ?? 30 };
  barrage = { s0: 0.1, s1: 3.4 };
  /** 스킬 6 공중 난무: 도약 클립 구간(오르기·내려오기)에 난무(combo) 구간을 끼운다. 판정 틱 = 착지(duration-1) */
  private readonly airSeg: Segment = { clip: 'leap', s0: 0, hit: 0.5, s1: 1, anchor: 47, total: 48 };
  /** 스킬 7 대지 가르기: 낙화 착지 내려찍기 구간(제자리), 판정 틱 = 내려찍기(SPLIT_IMPACT) */
  private readonly splitSeg: Segment = { clip: 'atk3', s0: 0, hit: 0.5, s1: 1, anchor: 18, total: 45 };
  runRate = 1;
  private readonly gait = new DistanceGait();
  private groundContact: GroundContact | null = null;
  private poseTransition: PoseTransition | null = null;
  /** 대기↔달리기 상체 전환(골반·척추·목·팔·손). 회피 출구 전환이 쓰는 동안에는 쓰지 않는다. */
  private carryTransition: PoseTransition | null = null;
  private carryFrom = 0;
  /** 마지막으로 움직이던 보행 속도(m/s) — 정지할 때 상체 전환 시간을 고른다 */
  private gaitSpeed = 0;
  /** 타별 후딜 표시 끝 클립 시각(판정·궤적 분석값 s1과 별개, 칼이 바닥 위에 남는 데까지) */
  private readonly displayS1: number[] = [];
  private runDistance = new Float32Array(0);
  private idleGroundY = 0;
  private runGroundY = 0;
  /** 달리기 클립 발목 본 최저 높이(모델 단위, 배율 1) — 디딤 수평 고정의 판정 기준 */
  private readonly runAnkleMin = [0, 0];
  private readonly footBand: [number, number] = [0, 0];
  private readonly footLock = { active: false, dt: 0, band: this.footBand, turn: 0, stepAt: 0, settle: 0, settleTime: IDLE_SETTLE_TIME, stepTime: TURN_STEP_TIME, stepLift: TURN_STEP_LIFT, release: false };
  /** 달리기 몸 방향과 실제 이동 방향의 차이(rad, 0..π). HeroVisual이 update 전에 넘긴다(회전 디딤 판단). */
  private turnError = 0;
  /** 대기 자세를 재생한 시간(영웅 초). 대기가 아니면 0 */
  private idleAge = 0;
  /** 달리기 자세를 재생한 시간(영웅 초). 달리기가 아니면 0 */
  private runAge = 0;
  private soleSamples: number[] = [];
  /** 보고·QA용 표 */
  readonly table: Record<string, string | number>[] = [];
  faceFlip = false;

  private cur: THREE.AnimationAction | null = null;
  private curKey = '';
  private attackSerial = 0;
  private readonly attackRecovery = new AttackRecoveryGate();
  private lastStep = -1;
  private lastT = 0;
  private lastAct = -1;
  /** 직전 프레임 공격의 연계 단계와 경과 틱(회피 진입 섞기 길이를 게임 공격 단계로 고른다). 공격이 아니면 -1 */
  private lastAttackStep = -1;
  private lastAttackTick = 0;

  /** false면 방어구 단계 몸 교체(kael_t2·t3)를 하지 않는다: 다른 골격의 몸(라이라)에 카엘 단계 몸을 붙이지 않게 */
  private readonly tiersEnabled: boolean;

  constructor(gltf: GLTF, private readonly rim: RimControl, castShadow: boolean, opts: { readonly tiers?: boolean } = {}) {
    this.tiersEnabled = opts.tiers ?? true;
    const scene = gltf.scene;
    let mesh: THREE.SkinnedMesh | null = null;
    scene.traverse((o) => { if ((o as THREE.SkinnedMesh).isSkinnedMesh && !mesh) mesh = o as THREE.SkinnedMesh; });
    if (!mesh) throw new Error('Kael GLB에 SkinnedMesh가 없다');
    this.mesh = mesh;
    const m = this.mesh;
    scene.updateMatrixWorld(true);
    this.restMeshMatrix = m.matrixWorld.clone();
    this.restRigMatrix = m.parent!.matrixWorld.clone();
    for (const bone of m.skeleton.bones) this.restJoints.set(bone.name, bone.getWorldPosition(new THREE.Vector3()));

    // 툰 셰이딩(텍스처 색 유지) + 상시 림
    const src = m.material as THREE.MeshStandardMaterial;
    this.toon = createKaelSurface(src, rim);
    const fres = float(1).sub(normalView.dot(positionViewDirection).clamp(0, 1)).pow(3);
    m.material = this.toon;
    src.dispose();
    m.castShadow = castShadow;
    m.receiveShadow = true;
    m.frustumCulled = false;

    // 스키닝된 반전 헐 외곽선: 같은 지오메트리·골격, 스키닝 뒤 법선 방향으로 부풀린 뒷면.
    // 스키닝 행렬에 리그 축척(0.01)과 그 역이 섞여 법선 길이가 1이 아니므로 정규화한 뒤 두께를 곱한다.
    this.hullMat = new THREE.MeshBasicNodeMaterial({ color: 0x0b0f1c, side: THREE.BackSide });
    maskKaelOutline(this.toon, this.hullMat);
    this.hullMat.positionNode = positionLocal.add(normalLocal.normalize().mul(this.thickness));
    this.hull = new THREE.SkinnedMesh(m.geometry, this.hullMat);
    this.hull.bind(m.skeleton, m.bindMatrix);
    this.hull.position.copy(m.position);
    this.hull.quaternion.copy(m.quaternion);
    this.hull.scale.copy(m.scale);
    this.hull.frustumCulled = false;
    m.parent!.add(this.hull);

    // X-ray 실루엣: 영웅(renderOrder 2)보다 먼저(1), 이미 그려진 맵·몹·보스보다 '뒤'인 픽셀에만 그린다(depth Greater).
    // 불투명 파이프라인에서 1px 체크무늬로 반을 버려 뒤의 가림체가 비쳐 보이게 한다. 발바닥이 바닥과 겹친 곳은 깊이 오프셋으로 뺀다.
    this.xrayMat = new THREE.MeshBasicNodeMaterial({ depthWrite: false, depthFunc: THREE.GreaterDepth });
    this.xrayMat.polygonOffset = true;
    this.xrayMat.polygonOffsetFactor = -1;
    this.xrayMat.polygonOffsetUnits = -8;
    // 윤곽(프레넬 높은 곳)은 진한 흰 테두리로 꽉 채우고, 안쪽은 진홍 체크무늬로 비친다
    const edge = step(0.42, fres);
    this.xrayMat.colorNode = mix(vec3(1.0, 0.16, 0.26), vec3(1.0, 0.95, 0.95), edge);
    this.xrayMat.opacityNode = max(edge, floor(screenCoordinate.x).add(floor(screenCoordinate.y)).mod(2));
    this.xrayMat.alphaTest = 0.5;
    this.xrayMat.fog = false;
    this.xray = new THREE.SkinnedMesh(m.geometry, this.xrayMat);
    this.xray.bind(m.skeleton, m.bindMatrix);
    this.xray.position.copy(m.position);
    this.xray.quaternion.copy(m.quaternion);
    this.xray.scale.copy(m.scale);
    this.xray.frustumCulled = false;
    this.xray.renderOrder = 1;
    m.renderOrder = 2;
    // 몸·검(2)이 깊이/몸 마스크를 기록한 다음 바깥 헐만 그린다.
    this.hull.renderOrder = 3;
    m.parent!.add(this.xray);

    const find = (re: RegExp): THREE.Bone => {
      const b = m.skeleton.bones.find((x) => re.test(x.name));
      if (!b) throw new Error('본 없음: ' + re);
      return b;
    };
    this.bones = {
      hand: find(/RightHand$/), hips: find(/Hips$/), lFoot: find(/LeftFoot$/), rFoot: find(/RightFoot$/),
      lToe: find(/LeftToeBase$/), lUpLeg: find(/LeftUpLeg$/), rUpLeg: find(/RightUpLeg$/),
    };
    {
      // 왼손 주먹 중심 = 중지 첫 마디와 굽힌 끝 마디의 가운데(calibrateRunCarry의 오른손 주먹과 같은 정의)
      const lA = m.skeleton.bones.find(b => /LeftHandMiddle1$/.test(b.name));
      const lB = m.skeleton.bones.find(b => /LeftHandMiddle3$/.test(b.name));
      this.gripBones = lA && lB ? { lA, lB } : null;
    }

    // 대검: 에셋 레인 대검 GLB(T1). 못 읽으면 절차 대검(진홍 날 + 흰 날끝). 손잡이 원점, 날은 +Z(m)
    const gsword = swordFromGltf((gltf.userData.sword as GLTF | null | undefined) ?? null);
    this.swordGeo = gsword ? gsword.geometry : merge([
      colored(new THREE.CylinderGeometry(0.022, 0.026, 0.3, 8).rotateX(Math.PI / 2), 0x2a1d22, 0, 0, -0.05),
      colored(new THREE.SphereGeometry(0.04, 8, 6), PALETTE.gold, 0, 0, -0.21),
      colored(new THREE.BoxGeometry(0.34, 0.05, 0.07), PALETTE.gold, 0, 0, 0.12),
      colored(new THREE.BoxGeometry(0.1, 0.022, 1.05), 0xc4162a, 0, 0, 0.68),
      colored(new THREE.BoxGeometry(0.018, 0.016, 1.05), 0xffffff, 0.058, 0, 0.68),
      colored(new THREE.BoxGeometry(0.018, 0.016, 1.05), 0xffffff, -0.058, 0, 0.68),
      colored(new THREE.ConeGeometry(0.068, 0.14, 4).rotateX(Math.PI / 2).scale(1, 0.25, 1), 0xffffff, 0, 0, 1.27),
    ]);
    this.swordMat = gsword
      // 환경 맵이 없는 장면이라 금속도가 높으면 강철 날이 검게 죽는다(2026-10-14 근접 캡처): 낮게 두고 림으로 윤곽을 살린다
      ? new THREE.MeshStandardNodeMaterial({ color: 0xffffff, roughness: 0.45, metalness: 0.15, map: gsword.map })
      : new THREE.MeshStandardNodeMaterial({ color: 0xffffff, roughness: 0.35, metalness: 0.45, vertexColors: true });
    this.swordMat.emissive = new THREE.Color(0x000000);
    // 대검 판독성(dream-loop 마을 4회차 P1 "검이 어둡고 색 분리가 없다"): 그늘에서도 진홍 날이 읽히게 텍스처 색을 조금 자체 발광으로 더하고
    // 날 가장자리에 흰 반사선(시선과 비스듬한 면일수록 밝게)을 얹는다. 목표 화면의 "진홍 날 + 흰 날끝"을 따른다.
    // 칼날 몸통 단계 빛(2026-10-19 정본 캡처: 가장자리 프레넬만으로는 낮 장면에서 T1·T2·T3가 구별되지 않았다).
    // 대검 로컬 +Z가 칼날 방향(손잡이 원점, 가드 약 0.12m, 끝 1.32m)이라 가드 위부터만 더한다.
    const bladeMask = smoothstep(0.2, 0.36, positionLocal.z);
    // 햇빛을 정면으로 받는 칼날은 거의 흰색으로 포화돼 자체발광 색이 묻힌다(2026-10-19 정본 3차 T1→T2). 받는 빛의 색 자체에 단계 틴트를 곱한다.
    this.swordMat.colorNode = materialColor.mul(vec4(mix(vec3(1, 1, 1), this.swordTint, bladeMask), 1));
    this.swordMat.emissiveNode = gsword
      ? materialEmissive
        .add(gsword.map ? texture(gsword.map, uv()).rgb.mul(this.swordGlow) : vec3(0))
        .add(this.swordEdge.mul(fres))
        .add(this.swordBlade.mul(bladeMask))
        .add(rim.color.mul(fres).mul(rim.strength))
      : materialEmissive.add(vertexColor().mul(0.28)).add(this.swordEdge.mul(fres).mul(step(1.5, this.swordTierF))).add(this.swordBlade.mul(bladeMask)).add(rim.color.mul(fres).mul(rim.strength));
    const sword = new THREE.Mesh(this.swordGeo, this.swordMat);
    sword.castShadow = castShadow;
    sword.receiveShadow = true;
    sword.renderOrder = 2;
    const swordHull = new THREE.Mesh(this.swordGeo, new THREE.MeshBasicNodeMaterial({ color: 0x0b0f1c, side: THREE.BackSide }));
    (swordHull.material as THREE.MeshBasicNodeMaterial).positionNode = positionLocal.add(normalLocal.mul(KAEL_SWORD_OUTLINE_M));
    swordHull.renderOrder = 2;
    sword.add(swordHull);
    this.swordHull = swordHull;
    this.swordMesh = sword;
    this.tipMark.position.set(0, 0, 1.32);
    this.baseMark.position.set(0, 0, 0.62);
    sword.add(this.tipMark, this.baseMark);
    this.swordMount.add(sword);
    this.bones.hand.add(this.swordMount);

    this.orient.add(scene);
    this.root.add(this.orient);
    this.root.updateMatrixWorld(true);
    this.bones.hand.getWorldScale(this.v);
    this.handScale = this.v.x || 0.01;
    this.swordMount.scale.setScalar(1 / this.handScale);
    // 손 본 기준: +Y 손가락, +X 엄지 쪽. 손잡이를 손바닥 쪽(-Z 3cm)에 두고, 칼날은 Mixamo 대검 클립의 두 손 축에 맞춘다:
    // 오른손→왼손 방향(대기·공격·폭풍 평균 약 (-19,4,17)cm)의 반대가 칼날. 예전 (0,90,0)은 그 축에서 40~46° 비틀려
    // 왼손이 칼자루를 놓치고 칼날이 몸을 뚫었다(칼날·왼손 각도 대기 134°→176°, qa/assets/kael-rerig/HANDOFF-KAEL.md)
    this.setSwordMount(0, 9, -3, -13, 133, 0);

    this.mixer = new THREE.AnimationMixer(scene);
    for (const source of gltf.animations) {
      const c = source.name === 'run' ? source.clone() : source;
      if (c.name === 'run') {
        // 원본 195개 트랙 모두 1/30초에서 시작한다. 이 앞의 빈 시간은 오른발 디딤을 매 바퀴 멈춰 미끄러뜨린다.
        // 공통 시작 여백만 제거한다. 끝 자세는 첫 자세와 같으며 공격 시각·원본 GLB는 그대로 둔다.
        const first = Math.min(...c.tracks.map(track => track.times[0]));
        if (first > 0) { for (const track of c.tracks) track.shift(-first); c.resetDuration(); }
      }
      this.clips.set(c.name, c);
      this.actions.set(c.name, this.mixer.clipAction(c));
    }

    // 정면 확인: 발가락이 -Z를 보면 반 바퀴 돌린다(hero.facing 0 = +z)
    if (this.clips.has('idle')) this.pose('idle', 0);
    const toe = this.bones.lToe.getWorldPosition(new THREE.Vector3());
    const foot = this.bones.lFoot.getWorldPosition(new THREE.Vector3());
    this.faceFlip = toe.z - foot.z < 0;
    this.orient.rotation.y = this.faceFlip ? Math.PI : 0;
    this.root.updateMatrixWorld(true);

    // 몸 이동을 곡선으로 뽑고 클립은 제자리로 만든다. 공격은 렌더가 곡선대로 몸을 옮겨 디딘 발을 땅에 붙이고,
    // 회피·스킬·각성·피격은 게임이 이동을 정하므로(회피 4m, 돌진 7m) 곡선을 쓰지 않고 몸이 게임 위치를 따라간다.
    // 예전에는 회피 클립의 구르기 이동이 게임 회피 4m 위에 겹쳐 몸이 게임 위치에서 최대 9m 떨어졌다가 끝나는 순간 돌아왔다.
    this.extractRootMotion(['atk1', 'atk2', 'atk3', 'atk4', 'dodge', 'dash', 'leap', 'combo', 'powerup', 'hurt', 'death', 'victory']);
    this.calibrateRunCarry();
    this.analyze();

    this.root.scale.setScalar(KAEL_SCALE);
    this.root.updateMatrixWorld(true);
    m.getWorldScale(this.v);
    this.thickness.value = OUTLINE_M / (this.v.x || 0.01);
    this.groundContact = new GroundContact(m);
    // 골반·척추도 포함한다: 대기→달리기 골반 섞기가 0.1초에 끝나면 상체 전체가 그 축으로 돌아 칼끝이 빨라졌다. 다리는 제외(디딤 고정이 맡는다).
    this.carryTransition = new PoseTransition(m.skeleton.bones.filter(b => /(Hips|Spine|Neck|Head|Shoulder|Arm|Hand)/.test(b.name)));
    // 판정/이동 구간은 원본에서 먼저 분석한다. 재저작은 깊은 복제한 표시 클립에만 설치한다.
    const sourceAttack2 = this.clips.get('atk2');
    const recovery = sourceAttack2 && createAttackRecoveryClip(sourceAttack2, m.skeleton);
    if (recovery) {
      holdArmTracks(recovery, segTime(this.attacks[1], TAIL_ARM_HOLD_TICK));
      this.actions.set(recovery.name, this.mixer.clipAction(recovery));
    }
    this.measureRecoveryClearance();
    const sourceDodge = this.clips.get('dodge');
    const groundedDodge = sourceDodge && createGroundedDodgeClip(sourceDodge);
    if (groundedDodge) {
      this.poseTransition = new PoseTransition(m.skeleton.bones);
      this.originalDodgeClip = sourceDodge;
      // QA는 원본 회피와 표시 보정 회피를 따로 비교한다. 실제 재생은 기존 dodge 키만 쓴다.
      this.actions.set('dodge-source', this.actions.get('dodge')!);
      this.clips.set('dodge', groundedDodge);
      this.actions.set('dodge', this.mixer.clipAction(groundedDodge));
      this.reset();
    }
    this.tierCache = new KaelTierCache<TierSurface>(
      { geometry: m.geometry, material: this.toon, soleSamples: this.soleSamples,
        idleGroundY: this.idleGroundY, runGroundY: this.runGroundY },
      tier => this.loadTier(tier), value => this.applyTier(value), value => {
        value.geometry.dispose(); value.material.map?.dispose(); value.material.normalMap?.dispose(); value.material.dispose();
      });
  }

  get tierState(): KaelTierState & { readonly weaponTier: GearTier } { return Object.freeze({ ...this.tierCache.state, weaponTier: this.weaponTierValue }); }

  setTier(tier: GearTier): Promise<void> { return this.tiersEnabled ? this.tierCache.setTier(tier) : Promise.resolve(); }

  get weaponTier(): GearTier { return this.weaponTierValue; }

  /** 장착 무기 단계의 칼날 빛. 새 재질을 만들지 않고 uniform 값만 바꾼다(T1 흰빛 / T2 청백 / T3 잿불 금빛) */
  setWeaponTier(tier: GearTier): void {
    if (tier === this.weaponTierValue) return;
    this.weaponTierValue = tier;
    this.applySwordLook();
  }

  /** 아바타 무기 외형(레인 E): 단계 빛 대신 쓸 칼날 빛. null이면 단계 빛으로 돌아간다(uniform 값만 바꾼다) */
  private swordLookOverride: { readonly edge: readonly [number, number, number]; readonly glow: readonly [number, number, number]; readonly blade: readonly [number, number, number]; readonly tint: readonly [number, number, number] } | null = null;
  setWeaponLookOverride(look: KaelModel['swordLookOverride']): void {
    this.swordLookOverride = look;
    this.applySwordLook();
  }
  private applySwordLook(): void {
    const look = this.swordLookOverride ?? SWORD_TIER_LOOK[this.weaponTierValue];
    this.swordEdge.value.set(...look.edge);
    this.swordGlow.value.set(...look.glow);
    this.swordBlade.value.set(...look.blade);
    this.swordTint.value.set(...look.tint);
    this.swordTierF.value = this.swordLookOverride ? Math.max(2, this.weaponTierValue) : this.weaponTierValue;
  }

  private async loadTier(tier: GearTier): Promise<TierSurface> {
    const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
    // 절대 경로여야 /tools/qa/ 아래 픽스처에서도 같은 에셋을 읽는다.
    const url = new URL(assetUrl(`characters/kael_t${tier}.glb`), document.baseURI);
    const gltf = await loader.loadAsync(url.href);
    gltf.scene.updateMatrixWorld(true);
    let source: THREE.SkinnedMesh | undefined;
    gltf.scene.traverse(o => { if (!source && (o as THREE.SkinnedMesh).isSkinnedMesh) source = o as THREE.SkinnedMesh; });
    if (!source) throw new Error(`T${tier} SkinnedMesh 없음`);
    const src = source;
    let geometry: THREE.BufferGeometry | undefined;
    let material: THREE.MeshToonNodeMaterial | undefined;
    try {
      const delta = (a: THREE.Matrix4, b: THREE.Matrix4) => Math.max(...a.elements.map((v, i) => Math.abs(v - b.elements[i])));
      // 재내보내기의 부동소수점 오차만 허용한다. 다른 리그를 조용히 같은 골격에 연결하지 않는다.
      const meshDelta = delta(this.restMeshMatrix, src.matrixWorld);
      const rigDelta = delta(this.restRigMatrix, src.parent!.matrixWorld);
      if (meshDelta > 1e-6 || rigDelta > 1e-6) throw new Error(`T${tier} 노드 변환 불일치: mesh=${meshDelta}, rig=${rigDelta}`);
      let restJointMaxM = 0;
      const point = new THREE.Vector3();
      for (const bone of src.skeleton.bones) {
        const target = this.restJoints.get(bone.name);
        if (!target) throw new Error(`T1에 없는 단계 본: ${bone.name}`);
        restJointMaxM = Math.max(restJointMaxM, bone.getWorldPosition(point).distanceTo(target));
      }
      if (restJointMaxM > 0.001) throw new Error(`T${tier} rest 관절 불일치: ${restJointMaxM}m`);
      geometry = rebindTierGeometry(src, this.mesh);
      Object.assign(geometry.userData.tierBinding, { meshDelta, rigDelta, bones: src.skeleton.bones.length, restJointMaxM });
      material = createKaelSurface(src.material as THREE.MeshStandardMaterial, this.rim);
      maskKaelOutline(material, this.hullMat);
      const ground = measureTierGround(this.mixer.getRoot() as THREE.Object3D, geometry, this.clips);
      return { geometry, material, ...ground };
    } catch (error) {
      geometry?.dispose(); material?.dispose();
      const original = src.material as THREE.MeshStandardMaterial;
      original.map?.dispose(); original.normalMap?.dispose();
      throw error;
    } finally {
      src.geometry.dispose(); src.skeleton.dispose();
      (src.material as THREE.Material).dispose();
    }
  }

  /** 같은 재질·골격·변환·표시 상태로 새 SkinnedMesh를 만들어 교체한다(이전 객체는 장면에서 뺀다) */
  private replaceSkinned(old: THREE.SkinnedMesh, geometry: THREE.BufferGeometry, material: THREE.Material | THREE.Material[] = old.material): THREE.SkinnedMesh {
    const next = new THREE.SkinnedMesh(geometry, material);
    next.name = old.name;
    next.bind(this.mesh.skeleton, this.mesh.bindMatrix);
    next.position.copy(old.position); next.quaternion.copy(old.quaternion); next.scale.copy(old.scale);
    next.frustumCulled = old.frustumCulled; next.renderOrder = old.renderOrder; next.visible = old.visible;
    next.castShadow = old.castShadow; next.receiveShadow = old.receiveShadow;
    const parent = old.parent!;
    parent.remove(old); parent.add(next);
    return next;
  }

  private applyTier(value: TierSurface): void {
    this.groundContact?.restore();
    value.material.emissive.copy(this.toon.emissive);
    value.material.emissiveIntensity = this.toon.emissiveIntensity;
    this.toon = value.material;
    // geometry만 바꾸면 WebGPU가 이전 정점 형식(T1 양자화 int16)으로 만든 파이프라인(외곽선 헐·x-ray·그림자 깊이 패스)을 재사용해
    // 새 Float32 정점을 잘못 읽는다(실측: 단계 교체 뒤 몸 둘레 큰 검은 덩어리와 커다란 그림자). 몸·헐·x-ray 모두 새 객체로 바꿔 파이프라인을 다시 만든다.
    this.mesh = this.replaceSkinned(this.mesh, value.geometry, value.material);
    this.hull = this.replaceSkinned(this.hull, value.geometry);
    this.xray = this.replaceSkinned(this.xray, value.geometry);
    // skeleton·bindMatrix·mixer·클립·검 소켓은 T1 그대로 두고 정점 번호에 의존하는 접지만 교체한다.
    this.root.updateMatrixWorld(true);
    this.mesh.computeBoundingBox(); this.mesh.computeBoundingSphere();
    // 세 메시의 geometry·골격·변환이 같으므로 전체 정점 순회는 본체 한 번으로 충분하다.
    this.hull.boundingBox = this.mesh.boundingBox.clone(); this.xray.boundingBox = this.mesh.boundingBox.clone();
    this.hull.boundingSphere = this.mesh.boundingSphere.clone(); this.xray.boundingSphere = this.mesh.boundingSphere.clone();
    this.soleSamples = value.soleSamples;
    this.idleGroundY = value.idleGroundY; this.runGroundY = value.runGroundY;
    this.root.updateMatrixWorld(true);
    this.groundContact = new GroundContact(this.mesh);
  }

  /** 별도 측정 페이지 전용: 런타임 보정 액션까지 같은 mixer에서 직접 샘플한다. */
  /** 따로 받은 클립(골격·클립만 든 GLB)을 더한다. 이미 있는 이름은 건드리지 않는다 */
  addClips(clips: readonly THREE.AnimationClip[]): void {
    for (const c of clips) {
      if (this.clips.has(c.name)) continue;
      this.clips.set(c.name, c);
      this.actions.set(c.name, this.mixer.clipAction(c));
    }
  }

  qaTierClips(): readonly { name: string; duration: number }[] {
    return [...this.actions].map(([name, action]) => ({ name, duration: action.getClip().duration }));
  }

  qaTierPose(name: string, time: number): void {
    this.pose(name, time);
    if (name === 'run') {
      this.swordMount.position.copy(this.runMountP); this.swordMount.quaternion.copy(this.runMountQ);
    } else {
      this.swordMount.position.copy(this.mountP); this.swordMount.quaternion.copy(this.mountQ);
    }
    this.root.updateMatrixWorld(true); this.mesh.skeleton.update();
  }

  /** QA 계측(?qa·개발 서버): 칼끝·왼발·오른발 월드 위치를 out[0..8]에 쓴다. 렌더가 행렬을 갱신한 뒤 부른다. */
  qaSample(out: Float32Array): void {
    this.tipMark.getWorldPosition(this.v); out[0] = this.v.x; out[1] = this.v.y; out[2] = this.v.z;
    this.bones.lFoot.getWorldPosition(this.v); out[3] = this.v.x; out[4] = this.v.y; out[5] = this.v.z;
    this.bones.rFoot.getWorldPosition(this.v); out[6] = this.v.x; out[7] = this.v.y; out[8] = this.v.z;
  }

  /** 대검 장착 자세(손 본 기준 위치 cm, 회전 도). 바꾸면 칼끝 궤적을 다시 계산한다. */
  setSwordMount(px: number, py: number, pz: number, rx: number, ry: number, rz: number, reanalyze = false): void {
    this.swordMount.position.set(px, py, pz);
    this.swordMount.rotation.set((rx * Math.PI) / 180, (ry * Math.PI) / 180, (rz * Math.PI) / 180);
    this.mountQ.copy(this.swordMount.quaternion);
    this.mountP.copy(this.swordMount.position);
    if (reanalyze) {
      const s = this.root.scale.x;
      const displayDodge = this.clips.get('dodge'), displayAction = this.actions.get('dodge');
      this.root.scale.setScalar(1);
      if (this.originalDodgeClip) {
        this.clips.set('dodge', this.originalDodgeClip);
        this.actions.set('dodge', this.mixer.clipAction(this.originalDodgeClip));
      }
      try { this.analyze(); }
      finally {
        if (displayDodge && displayAction) {
          this.clips.set('dodge', displayDodge);
          this.actions.set('dodge', displayAction);
        }
        this.root.scale.setScalar(s);
      }
      this.root.updateMatrixWorld(true);
      this.cur = null;
      this.curKey = '';
    }
  }

  /** 원본 Run With Sword의 주먹: 검지→소지 줄이 손잡이 축, 굽힌 중지 안쪽이 손잡이 중심이다. */
  private calibrateRunCarry(): void {
    this.runMountQ.copy(this.mountQ);
    this.runMountP.copy(this.mountP);
    const find = (suffix: string) => this.mesh.skeleton.bones.find(b => b.name.endsWith(suffix));
    const index = find('RightHandIndex1'), pinky = find('RightHandPinky1');
    const middle = find('RightHandMiddle1'), curled = find('RightHandMiddle3');
    if (!index || !pinky || !middle || !curled || !this.clips.has('run')) return;
    this.pose('run', 0);
    const hand = this.bones.hand;
    const local = (bone: THREE.Bone) => hand.worldToLocal(bone.getWorldPosition(new THREE.Vector3()));
    const across = local(index).sub(local(pinky)).normalize();
    const finger = local(middle), inside = local(curled);
    this.runMountP.copy(finger).add(inside).multiplyScalar(0.5);
    // 날 +Z를 주먹의 소지→검지 축에, 날 폭 +X를 손가락 방향에 맞춘다. 검이 주먹에서 뒤집혀 아래로 늘어지지 않는다.
    const width = finger.clone().addScaledVector(across, -finger.dot(across)).normalize();
    const normal = new THREE.Vector3().crossVectors(across, width).normalize();
    this.runMountQ.setFromRotationMatrix(new THREE.Matrix4().makeBasis(width, normal, across));
  }

  // ── 분석 ────────────────────────────────────────────
  private pose(name: string, time: number): void {
    // 분석·QA 자세는 스미어 없이 원본 칼 길이로 잰다(궤적·바닥 여유)
    if (this.swordMesh) this.swordMesh.scale.z = 1;
    this.groundContact?.restore();
    this.poseTransition?.reset();
    this.carryTransition?.reset();
    this.mixer.stopAllAction();
    const a = this.actions.get(name);
    if (!a) return;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    a.timeScale = 0;
    a.play();
    a.time = Math.min(time, a.getClip().duration);
    this.mixer.update(0);
    this.root.updateMatrixWorld(true);
    // 분석(칼끝 궤적·바닥 여유)도 화면과 같은 양손 소켓으로 잰다
    if (name !== 'run') this.applyTwoHandGrip(this.mountQ, this.mountP);
  }

  /**
   * 날 세우기: 소켓(applyTwoHandGrip 뒤)에 칼날 축 비틀림만 더한다. on이 아니면(대기·달리기·회피) 원본 비틀림으로 돌아간다.
   * 칼끝 속도는 루트 기준(게임 이동 제외, 몸 방향 회전 포함). 칼날 축 위 칼끝은 비틀림으로 움직이지 않아 되먹임이 없다.
   */
  /** QA 계측: 분석 자세(pose) 뒤 화면 재생과 같은 양손 쥐기(각속도 상한 포함)·날 세우기를 dt로 한 프레임 적용한다 */
  qaDisplayGrip(dt: number, on: boolean): void {
    if (!on) this.gripHasPrev = false;
    this.applyTwoHandGrip(this.mountQ, this.mountP, on ? dt : 0);
    this.applyEdgeRoll(dt, on);
  }

  applyEdgeRoll(dt: number, on: boolean): void {
    const mount = this.swordMount;
    if (!this.twoHandGrip || dt <= 0) { if (this.edgeRoll !== 0) mount.quaternion.multiply(this.edgeQ.setFromAxisAngle(this.edgeZ, this.edgeRoll)); return; }
    mount.updateWorldMatrix(true, false);
    // 현재 소켓(비틀림 전)의 날 폭 축(x)·두께 축(y)·칼날 축(z), 루트 기준
    this.gripInv.copy(this.root.matrixWorld).invert();
    this.tipMark.getWorldPosition(this.gripT).applyMatrix4(this.gripInv);
    let target = 0, follow = EDGE_RELEASE;
    if (on && this.edgeHasPrev) {
      this.gripD.subVectors(this.gripT, this.edgePrev).multiplyScalar(1 / dt);
      // 루트 역행렬이 KAEL_SCALE을 이미 빼서 원본 배율 m/s다
      const sp = this.gripD.length();
      const w = clamp01((sp - EDGE_V0) / (EDGE_V1 - EDGE_V0));
      if (w > 0) {
        this.gripQ.setFromRotationMatrix(this.gripInv.multiply(mount.matrixWorld));
        this.gripL.set(0, 0, 1).applyQuaternion(this.gripQ);
        this.gripD.addScaledVector(this.gripL, -this.gripD.dot(this.gripL));
        this.gripB.set(1, 0, 0).applyQuaternion(this.gripQ);
        this.gripL.cross(this.gripB); // 두께 축 y = z × x
        // 휘두름 방향이 날 폭 축(x)에서 몇 도인가: 양날이라 ±90° 안으로 접는다
        let phi = Math.atan2(this.gripD.dot(this.gripL), this.gripD.dot(this.gripB));
        if (phi > Math.PI / 2) phi -= Math.PI; else if (phi < -Math.PI / 2) phi += Math.PI;
        // 이미 더해 둔 비틀림에서 이어지는 쪽(접힘 ±π 중 가까운 쪽)
        target = phi;
        if (target - this.edgeRoll > Math.PI / 2) target -= Math.PI; else if (target - this.edgeRoll < -Math.PI / 2) target += Math.PI;
        target = Math.max(-EDGE_MAX, Math.min(EDGE_MAX, target));
        follow = EDGE_FOLLOW * w;
      }
    }
    if (!on) this.edgeHasPrev = false;
    else { this.edgePrev.copy(this.gripT); this.edgeHasPrev = true; }
    const step = (target - this.edgeRoll) * (1 - Math.exp(-follow * dt)), lim = EDGE_RATE * dt;
    this.edgeRoll += Math.max(-lim, Math.min(lim, step));
    if (Math.abs(this.edgeRoll) < 1e-4) this.edgeRoll = 0;
    if (this.edgeRoll !== 0) mount.quaternion.multiply(this.edgeQ.setFromAxisAngle(this.edgeZ, this.edgeRoll));
  }

  /**
   * 대검 소켓을 base(손 본 기준 한 손 소켓)에서 양손 쥐기로 옮긴다: 칼날 축을 왼손 주먹 → 오른손 손잡이점으로 돌리고(최소 회전, 날 비틀림 유지)
   * 손잡이 중심을 두 주먹 가운데 쪽으로 미끄러뜨린다. 손 본 행렬만 갱신한다(조상 사슬). 프레임 루프에서 새 객체를 만들지 않는다.
   */
  private applyTwoHandGrip(baseQ: THREE.Quaternion, baseP: THREE.Vector3, dt = 0): void {
    const mount = this.swordMount;
    this.gripBaseQ.copy(baseQ); this.gripBaseP.copy(baseP);
    mount.quaternion.copy(this.gripBaseQ); mount.position.copy(this.gripBaseP);
    this.twoHandWeight = 0;
    const gb = this.gripBones;
    if (!gb || !this.twoHandGrip) { if (dt > 0) this.gripHasPrev = false; return; }
    const hand = this.bones.hand;
    gb.lA.updateWorldMatrix(true, false); gb.lB.updateWorldMatrix(true, false);
    hand.updateWorldMatrix(true, false);
    this.gripInv.copy(hand.matrixWorld).invert();
    this.gripL.setFromMatrixPosition(gb.lA.matrixWorld);
    this.gripT.setFromMatrixPosition(gb.lB.matrixWorld);
    this.gripL.add(this.gripT).multiplyScalar(0.5).applyMatrix4(this.gripInv);
    // 칼날 축 = 왼손 주먹 → 오른손 손잡이점(소켓 위치). 왼손 주먹이 칼날 축 위(자루)에 온다.
    this.gripD.subVectors(this.gripBaseP, this.gripL);
    // 손 본 단위(분석 공간 handScale m/단위)로 잰 두 주먹 거리 → 원본 배율 m
    const sep = this.gripD.length() * this.handScale;
    let w = 0;
    this.gripQ.identity();
    if (sep > 1e-4) {
      const wSep = 1 - clamp01((sep - TWO_HAND_NEAR) / (TWO_HAND_FAR - TWO_HAND_NEAR));
      this.gripD.multiplyScalar(1 / (sep / this.handScale));
      this.gripB.set(0, 0, 1).applyQuaternion(this.gripBaseQ);
      const deg = Math.acos(Math.max(-1, Math.min(1, this.gripB.dot(this.gripD)))) * (180 / Math.PI);
      const wAng = 1 - clamp01((deg - TWO_HAND_MAX_DEG * 0.7) / (TWO_HAND_MAX_DEG * 0.3));
      w = Math.max(0, wSep * wAng);
      this.twoHandDeg = deg;
      if (w > 0) {
        this.gripAimQ.setFromUnitVectors(this.gripB, this.gripD);
        // slerpQuaternions는 this를 먼저 qa로 덮으므로 목표는 따로 둔다(같은 객체를 넘기면 항상 단위 회전이 된다)
        this.gripQ.slerpQuaternions(this.gripIdQ, this.gripAimQ, w);
      }
    }
    // 화면 재생(dt>0): 클립의 왼손이 한 프레임에 튀면(4타 회전 27f: 보정 10°→39°) 칼끝이 81m/s로 홱 돌았다. 보정 회전의 변화를 각속도 상한으로 묶는다
    // (켜지고 꺼질 때도 같은 상한으로 잇는다). 분석 자세(dt=0)는 상한 없이 그대로 잰다(궤적 리본 기준).
    if (dt > 0) {
      if (this.gripHasPrev) this.gripPrevQ.rotateTowards(this.gripQ, TWO_HAND_RATE * dt);
      else this.gripPrevQ.copy(this.gripQ);
      this.gripHasPrev = true;
      this.gripQ.copy(this.gripPrevQ);
    }
    this.twoHandWeight = w;
    mount.quaternion.copy(this.gripQ).multiply(this.gripBaseQ);
    // 손잡이 중심(검 원점)을 두 주먹 가운데 쪽으로: 오른손은 코등이 아래, 왼손은 폼멜 위 손잡이에 온다
    const slide = Math.min(TWO_HAND_SLIDE_M, sep * 0.5) / this.handScale * w;
    if (slide > 0) {
      this.gripT.set(0, 0, -1).applyQuaternion(mount.quaternion);
      mount.position.copy(this.gripBaseP).addScaledVector(this.gripT, slide);
    }
  }


  private frames(name: string): number {
    const c = this.clips.get(name);
    return c ? Math.max(4, Math.floor(c.duration * 60)) : 0;
  }

  /**
   * 클립의 몸 이동(엉덩이 수평 위치)을 60Hz로 뽑아 rootMotion에 두고, 클립의 엉덩이 수평 위치는 대기 첫 자세 자리에 고정한다.
   * 예전에는 몸 이동이 렌더에 그대로 남아 게임 돌진과 따로 놀았다: 4타가 끝나면 몸이 게임 위치보다 3.5m 앞에 있다가
   * 대기로 섞이는 0.16초 동안 뒤로 미끄러져 돌아왔고(약 20m/s), 4타 시작 자세가 2m 앞이라 3타→4타 전환에서 몸이 튀었다.
   * 분석 공간(루트 단위 행렬·배율 1)에서 잰다. 엉덩이 부모(Armature)는 움직이지 않는다고 본다.
   */
  private extractRootMotion(names: readonly string[]): void {
    const hips = this.bones.hips, parent = hips.parent;
    if (!parent || !this.clips.has('idle')) return;
    this.pose('idle', 0);
    hips.getWorldPosition(this.v);
    const rx = this.v.x, rz = this.v.z;
    const p = new THREE.Vector3();
    for (const name of names) {
      const clip = this.clips.get(name);
      if (!clip) continue;
      const n = this.frames(name);
      const d = new Float32Array((n + 1) * 2);
      for (let i = 0; i <= n; i++) {
        this.pose(name, Math.min(i * TICK, clip.duration));
        hips.getWorldPosition(this.v);
        d[i * 2] = this.v.x - rx; d[i * 2 + 1] = this.v.z - rz;
      }
      this.rootMotion.set(name, d);
      const track = clip.tracks.find((t) => t.name === hips.name + '.position');
      if (!track) continue;
      parent.updateWorldMatrix(true, false);
      const pm = parent.matrixWorld.clone(), inv = pm.clone().invert();
      const vals = track.values;
      for (let k = 0; k + 2 < vals.length; k += 3) {
        p.set(vals[k], vals[k + 1], vals[k + 2]).applyMatrix4(pm);
        p.x = rx; p.z = rz;
        p.applyMatrix4(inv);
        vals[k] = p.x; vals[k + 1] = p.y; vals[k + 2] = p.z;
      }
    }
  }

  private swing(name: string): Swing | null {
    const clip = this.clips.get(name);
    if (!clip) return null;
    const n = this.frames(name);
    const p = new Float32Array((n + 1) * 3);
    // 제자리로 만든 클립이라도 화면에서는 몸 이동 곡선만큼 몸이 움직인다: 손 속도에 몸 이동을 더해 타격 프레임을 고른다
    const rm = this.rootMotion.get(name);
    const q = { x: 0, z: 0 };
    for (let i = 0; i <= n; i++) {
      this.pose(name, i * TICK);
      this.bones.hand.getWorldPosition(this.v);
      if (rm) rmAt(rm, i * TICK, q);
      p[i * 3] = this.v.x + q.x; p[i * 3 + 1] = this.v.y; p[i * 3 + 2] = this.v.z + q.z;
    }
    const sp = new Float32Array(n + 1);
    for (let i = 1; i < n; i++) {
      sp[i] = Math.hypot(p[(i + 1) * 3] - p[(i - 1) * 3], p[(i + 1) * 3 + 1] - p[(i - 1) * 3 + 1], p[(i + 1) * 3 + 2] - p[(i - 1) * 3 + 2]) * 30;
    }
    const sm = new Float32Array(n + 1);
    for (let i = 1; i < n; i++) sm[i] = (sp[i - 1] + 2 * sp[i] + sp[i + 1]) / 4;
    let best = 2;
    for (let i = 2; i < n - 1; i++) if (sm[i] > sm[best]) best = i;
    const peak = sm[best];
    const th = peak * 0.3;
    let a = best;
    while (a > 1 && sm[a - 1] > th && best - a < 27) a--;
    let b = best;
    while (b < n - 1 && sm[b + 1] > th && b - best < 24) b++;
    // 정착점: 휘두름 끝(b)부터 0.3초 안에서 손 속도가 가장 낮은 프레임
    let st = b;
    for (let i = b; i < Math.min(n, b + 18); i++) if (sm[i] < sm[st]) st = i;
    return { dur: clip.duration, hit: best * TICK, peak, sw0: a * TICK, sw1: b * TICK, settle: st * TICK };
  }

  private trail(name: string, sw0: number, sw1: number, tip: Float32Array, base: Float32Array): void {
    for (let i = 0; i < TRAIL_N; i++) {
      this.pose(name, sw0 + ((sw1 - sw0) * i) / (TRAIL_N - 1));
      this.tipMark.getWorldPosition(this.v);
      tip[i * 3] = this.v.x; tip[i * 3 + 1] = this.v.y; tip[i * 3 + 2] = this.v.z;
      this.baseMark.getWorldPosition(this.v);
      base[i * 3] = this.v.x; base[i * 3 + 1] = this.v.y; base[i * 3 + 2] = this.v.z;
    }
  }

  private analyze(): void {
    this.attacks.length = 0;
    this.table.length = 0;
    const r3 = (x: number) => Math.round(x * 1000) / 1000;
    // 1~4타
    for (let k = 0; k < 4; k++) {
      const name = 'atk' + (k + 1);
      const spec = COMBO[k];
      const s = this.swing(name) ?? { dur: 1, hit: 0.5, peak: 0, sw0: 0.4, sw1: 0.6, settle: 0.7 };
      // 마무리타(360 회전)는 회전 시작부터 보여 주도록 선딜 구간을 더 앞에서 자른다
      const s0 = Math.max(0, Math.min(s.sw0 - (spec.finisher ? 0.35 : 0.15), s.hit - TICK));
      // 구간 끝 = 휘두름이 멎는 정착점. 예전처럼 휘두름 끝 + 0.3초까지 넣으면 클립의 가드 복귀 동작까지
      // 후딜에 2~3배속으로 압축돼 마지막 몇 프레임에 칼이 튀었다(1타 칼끝 후딜 p95 = 판정 속도). 복귀는 대기 페이드가 맡는다.
      const s1 = Math.min(s.dur, Math.max(s.sw1, s.settle));
      const anchor = spec.windup;
      const total = spec.windup + spec.active + spec.recovery;
      const ratePre = (s.hit - s0) / (anchor * TICK);
      const ratePost = (s1 - s.hit) / ((total - anchor) * TICK);
      const tip = new Float32Array(TRAIL_N * 3), base = new Float32Array(TRAIL_N * 3);
      this.trail(name, s.sw0, s.sw1, tip, base);
      const headStart = s.sw1 > s.sw0 ? clamp01((s.hit - s.sw0) / (s.sw1 - s.sw0)) : 0;
      // 칼끝 리본은 곡선이 휘두름을 끝내는 틱 수(SWEEP_TICKS)만큼 그린다: 몸의 칼날과 리본 머리가 같은 틱에 끝난다
      const drawTicks = Math.max(2, Math.min(5, SWEEP_TICKS[k] ?? 4));
      const rm = this.rootMotion.get(name) ?? null;
      const p0 = { x: 0, z: 0 }, p1 = { x: 0, z: 0 };
      if (rm) { rmAt(rm, s0, p0); rmAt(rm, s1, p1); }
      this.attacks.push({ clip: name, s0, hit: s.hit, s1, anchor, total, dur: s.dur, peak: s.peak, sw0: s.sw0, sw1: s.sw1, ratePre, ratePost, tip, base, headStart, drawTicks, rm, rm0x: p0.x, rm0z: p0.z, curve: emptyCurve() });
      // 구간 동안 몸이 나아간 거리(앞방향 +z 성분, 화면 배율 적용). 게임 돌진(COMBO.lunge)과 같아야 끝에 남는 미끄러짐이 없다
      const rootNetM = r3((p1.z - p0.z) * KAEL_SCALE), rootSideM = r3((p1.x - p0.x) * KAEL_SCALE);
      this.table.push({ use: (k + 1) + '타', clip: name, dur: r3(s.dur), hitFrame: Math.round(s.hit * 30), hitSec: r3(s.hit), handPeakMs: r3(s.peak), swing: r3(s.sw0) + '~' + r3(s.sw1), segment: r3(s0) + '~' + r3(s1), anchorTick: anchor, totalTick: total, ratePre: r3(ratePre), ratePost: r3(ratePost), rootNetM, rootSideM, lungeM: spec.lunge });
    }
    this.buildAttackCurves();
    // 스킬 1 피의 돌진: 돌진 40% 지점에 타격 프레임
    const sk = (id: number) => SKILLS[id - 1];
    const d = this.swing('dash');
    if (d) {
      const total = sk(1).duration, anchor = Math.round(total * 0.4);
      this.dash = { clip: 'dash', s0: Math.max(0, d.sw0 - 0.2), hit: d.hit, s1: Math.min(d.dur, d.sw1 + 0.3), anchor, total };
      this.table.push(this.segRow('스킬1 돌진', this.dash, d));
    }
    // 스킬 3 붉은 낙화: 착지 틱(duration-1)에 타격 프레임
    const l = this.swing('leap');
    if (l) {
      const total = sk(3).duration, anchor = total - 1;
      this.leap = { clip: 'leap', s0: Math.max(0, l.hit - 1.1), hit: l.hit, s1: Math.min(l.dur, l.hit + 0.3), anchor, total };
      this.table.push(this.segRow('스킬3 낙화', this.leap, l));
    }
    // 스킬 4 진홍 초승달(0.15초 선딜 가로 휘두름): 전용 클립이 없어 2타 휘두름 구간을 쓴다(도약 클립으로 떨어지지 않게)
    const a2 = this.attacks[1];
    if (a2) {
      const total = SKILLS[3]?.duration ?? 33;
      this.crescent = { clip: a2.clip, s0: a2.s0, hit: a2.hit, s1: a2.s1, anchor: SKILL_TUNING.crescentWindup, total };
      this.table.push({ use: '스킬4 초승달', clip: a2.clip, segment: r3(a2.s0) + '~' + r3(a2.s1), anchorTick: SKILL_TUNING.crescentWindup, totalTick: total });
      // 올려베기(스킬 5)도 판정이 0.15초(초승달 선딜과 같은 9틱)라 같은 매듭을 쓰고 길이만 다르다
      buildAttackCurve(this.crescentCurve, a2.s0, a2.hit, a2.sw1, a2.s1, SKILL_TUNING.crescentWindup, total, SWEEP_TICKS[1], COIL[1]);
      buildAttackCurve(this.launcherCurve, a2.s0, a2.hit, a2.sw1, a2.s1, SKILL_TUNING.crescentWindup, SKILLS[4]?.duration ?? 30, SWEEP_TICKS[1], COIL[1]);
    }
    // 스킬 6 공중 난무(C9): 낙화와 같은 도약 구간을 6번 길이에 맞춘다. 판정(착지) 틱은 duration-1
    if (l && SKILLS[5]) Object.assign(this.airSeg, { clip: 'leap', s0: Math.max(0, l.hit - 1.1), hit: l.hit, s1: Math.min(l.dur, l.hit + 0.3), anchor: SKILLS[5].duration - 1, total: SKILLS[5].duration });
    // 스킬 7 대지 가르기(C9): 낙화(leap) 착지 내려찍기 구간을 제자리로 쓴다(뿌리 이동은 추출돼 몸은 뜨지 않는다).
    // 3타(atk3)는 가로 휘두름이라 판정 틱에 칼이 위를 향했다(2026-10-31 캡처 qa/3month/c9 touch skill7_t19). 칼끝이 바닥에 닿는 hit 프레임을 SPLIT_IMPACT_TICK에 맞추고 뒤는 눌러 유지
    if (l && SKILLS[6]) {
      Object.assign(this.splitSeg, { clip: 'leap', s0: Math.max(0, l.hit - 0.4), hit: l.hit, s1: Math.min(l.dur, l.hit + 0.3), anchor: SPLIT_IMPACT_TICK, total: SKILLS[6].duration });
      this.table.push(this.segRow('스킬7 대지 가르기', this.splitSeg, l));
    }
    // 스킬 2 칼날 폭풍: atk4(360 회전)의 회전 구간을 엉덩이 방향으로 찾아 반복
    this.analyzeSpin();
    // 회피: 엉덩이가 낮아졌다 돌아오는 구간
    this.analyzeDodge();
    // 달리기: 발 접지 구간 이동량으로 발 미끄러짐을 줄이는 배율
    this.analyzeRun();
    // 클립별 몸 이동(지운 양): 구간 전체에서 엉덩이가 처음 자리에서 가장 멀리 간 수평 거리(화면 배율)
    for (const [name, d] of this.rootMotion) {
      let far = 0;
      for (let i = 0; i < d.length; i += 2) far = Math.max(far, Math.hypot(d[i] - d[0], d[i + 1] - d[1]));
      this.table.push({ use: '몸 이동 제거', clip: name, maxTravelM: r3(far * KAEL_SCALE) });
    }
    const pu = this.clips.get('powerup');
    if (pu) this.powerup = { s0: 0, s1: Math.min(pu.duration, 2.4) };
    const cb = this.clips.get('combo');
    if (cb) this.barrage = { s0: 0.1, s1: Math.max(0.2, cb.duration - 0.15) };
    const preTicks = AWAKEN.gather + AWAKEN.flash + AWAKEN.cutin;
    this.table.push({ use: '각성 모으기~컷인', clip: 'powerup', segment: this.powerup.s0 + '~' + r3(this.powerup.s1), totalTick: preTicks, rate: r3(this.powerup.s1 / (preTicks * TICK)) });
    this.table.push({ use: '각성 난무', clip: 'combo', segment: this.barrage.s0 + '~' + r3(this.barrage.s1), totalTick: AWAKEN.barrage, rate: r3((this.barrage.s1 - this.barrage.s0) / (AWAKEN.barrage * TICK)) });
    this.mixer.stopAllAction();
  }

  private segRow(use: string, g: Segment, s: Swing): Record<string, string | number> {
    const r3 = (x: number) => Math.round(x * 1000) / 1000;
    return { use, clip: g.clip, dur: r3(s.dur), hitFrame: Math.round(s.hit * 30), hitSec: r3(s.hit), handPeakMs: r3(s.peak), segment: r3(g.s0) + '~' + r3(g.s1), anchorTick: g.anchor, totalTick: g.total, ratePre: r3((g.hit - g.s0) / Math.max(TICK, g.anchor * TICK)), ratePost: r3((g.s1 - g.hit) / Math.max(TICK, (g.total - g.anchor) * TICK)) };
  }

  private analyzeSpin(): void {
    const name = 'atk4';
    const n = this.frames(name);
    if (!n) return;
    const cum = new Float32Array(n + 1);
    let prev = 0;
    const a = new THREE.Vector3(), b = new THREE.Vector3();
    for (let i = 0; i <= n; i++) {
      this.pose(name, i * TICK);
      this.bones.lUpLeg.getWorldPosition(a);
      this.bones.rUpLeg.getWorldPosition(b);
      const ang = Math.atan2(b.x - a.x, b.z - a.z);
      if (i === 0) { cum[0] = 0; prev = ang; continue; }
      let dd = ang - prev;
      if (dd > Math.PI) dd -= Math.PI * 2;
      else if (dd < -Math.PI) dd += Math.PI * 2;
      cum[i] = cum[i - 1] + dd;
      prev = ang;
    }
    let maxAbs = 0, at = 0;
    for (let i = 0; i <= n; i++) if (Math.abs(cum[i]) > maxAbs) { maxAbs = Math.abs(cum[i]); at = i; }
    const turnDeg = Math.round((maxAbs * 180) / Math.PI);
    const total = SKILLS[1].duration;
    if (maxAbs > 5.2) {
      let i0 = 0, i1 = at;
      while (i0 < at && Math.abs(cum[i0]) < maxAbs * 0.03) i0++;
      while (i1 > i0 && Math.abs(cum[i1]) > maxAbs * 0.97) i1--;
      this.storm = { clip: name, a: i0 * TICK, b: (i1 + 1) * TICK, loops: 2, turnDeg, dir: cum[at] < 0 ? -1 : 1 };
    } else {
      const s = this.attacks[3];
      this.storm = { clip: name, a: s ? s.sw0 : 0.3, b: s ? s.sw1 : 1.2, loops: 1, turnDeg, dir: cum[at] < 0 ? -1 : 1 };
    }
    // 칼날 폭풍은 4타와 같은 클립을 쓰지만 액션은 따로 둔다. 같은 액션이면 4타 → 폭풍 전환에서 섞지 못하고
    // 클립 시각만 되감겨 자세가 한 틱에 튀었다(칼끝 8.7m/틱, 2026-10-14 실측).
    const src = this.clips.get(name);
    if (src && !this.actions.has(STORM_CLIP)) {
      const c = src.clone();
      c.name = STORM_CLIP;
      this.clips.set(STORM_CLIP, c);
      this.actions.set(STORM_CLIP, this.mixer.clipAction(c));
    }
    if (src) this.storm.clip = STORM_CLIP;
    const st = this.storm;
    this.table.push({ use: '스킬2 폭풍', clip: name, turnDeg, segment: Math.round(st.a * 1000) / 1000 + '~' + Math.round(st.b * 1000) / 1000 + ' x' + st.loops, totalTick: total, rate: Math.round(((st.b - st.a) * st.loops) / (total * TICK) * 1000) / 1000 });
  }

  private analyzeDodge(): void {
    const name = 'dodge';
    const n = this.frames(name);
    if (!n) return;
    const y = new Float32Array(n + 1);
    for (let i = 0; i <= n; i++) { this.pose(name, i * TICK); this.bones.hips.getWorldPosition(this.v); y[i] = this.v.y; }
    let mi = 0;
    for (let i = 0; i <= n; i++) if (y[i] < y[mi]) mi = i;
    const y0 = y[0];
    let i0 = 0;
    while (i0 < mi && y[i0] > y0 - 0.05) i0++;
    i0 = Math.max(0, i0 - 3);
    let i1 = mi;
    while (i1 < n && y[i1] < y0 - 0.04) i1++;
    const s0 = i0 * TICK, s1 = Math.min(this.clips.get(name)!.duration, i1 * TICK + 0.05);
    const ok = y0 - y[mi] > 0.15 && s1 - s0 > 0.2;
    this.dodge = ok ? { clip: name, s0, hit: (s0 + s1) / 2, s1, anchor: DODGE.duration / 2, total: DODGE.duration } : { clip: name, s0: 0.05, hit: 0.55, s1: 1.05, anchor: DODGE.duration / 2, total: DODGE.duration };
    const r3 = (x: number) => Math.round(x * 1000) / 1000;
    this.table.push({ use: '회피', clip: name, hipsDropM: r3(y0 - y[mi]), segment: r3(this.dodge.s0) + '~' + r3(this.dodge.s1), totalTick: DODGE.duration, rate: r3((this.dodge.s1 - this.dodge.s0) / (DODGE.duration * TICK)) });
  }

  private analyzeRun(): void {
    const name = 'run';
    const clip = this.clips.get(name);
    if (!clip) return;
    const dur = clip.duration, n = Math.ceil(dur * 120), dt = dur / n;
    const ly = new Float32Array(n + 1), lz = new Float32Array(n + 1), ry = new Float32Array(n + 1), rz = new Float32Array(n + 1);
    // 발목 본은 발바닥보다 약 18~20cm 높다. 실제 발/발가락 가중 정점으로 접지 높이를 잰다(로딩 때만).
    const indices = this.mesh.geometry.getAttribute('skinIndex'), weights = this.mesh.geometry.getAttribute('skinWeight');
    const footBones = new Set(this.mesh.skeleton.bones.flatMap((b, i) => /(?:Foot|ToeBase|Toe_End)$/.test(b.name) ? [i] : []));
    const soleVertices: number[] = [];
    for (let i = 0; i < indices.count; i++) {
      let weight = 0;
      for (let k = 0; k < 4; k++) if (footBones.has(indices.getComponent(i, k))) weight += weights.getComponent(i, k);
      if (weight > 0.5) soleVertices.push(i);
    }
    const support = new Set<number>();
    const vertices = new PoseVertexReader(this.mesh);
    const soleY = (): number => {
      let y = Infinity, lowest = -1;
      vertices.update();
      for (const i of soleVertices) {
        vertices.world(i, this.v);
        if (this.v.y < y) { y = this.v.y; lowest = i; }
      }
      if (lowest >= 0) support.add(lowest);
      return Number.isFinite(y) ? y : 0;
    };
    this.pose('idle', 0);
    let idleSole = soleY();
    const idle = this.clips.get('idle');
    if (idle) for (let i = 1; i <= 16; i++) { this.pose('idle', idle.duration * i / 16); idleSole = Math.min(idleSole, soleY()); }
    let runSole = Infinity, minY = Infinity;
    for (let i = 0; i <= n; i++) {
      this.pose(name, i * dt);
      this.bones.lFoot.getWorldPosition(this.v); ly[i] = this.v.y; lz[i] = this.v.z;
      this.bones.rFoot.getWorldPosition(this.v); ry[i] = this.v.y; rz[i] = this.v.z;
      minY = Math.min(minY, ly[i], ry[i]);
      runSole = Math.min(runSole, soleY());
    }
    this.runAnkleMin[0] = Math.min(...ly); this.runAnkleMin[1] = Math.min(...ry);
    this.soleSamples = [...support];
    // 원본의 체공·발끝 구름은 유지하고 지면 관통/대기 부양만 골반 전체의 높이로 보정한다.
    this.idleGroundY = Math.max(-0.04, Math.min(0.04, 0.004 - idleSole));
    this.runGroundY = Math.max(-0.04, Math.min(0.04, 0.004 - runSole));
    const speeds: number[] = [];
    const phaseSpeed = new Float32Array(n);
    const stanceL = new Uint8Array(n), stanceR = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      // 낮게 디딘 발의 뒤로 쓸리는 속도. 좌우 발을 따로 골라 비대칭 보폭도 실제 위상에 반영한다.
      const left = Math.max(ly[i], ly[i + 1]) < minY + 0.04;
      const right = Math.max(ry[i], ry[i + 1]) < minY + 0.04;
      const speed = left ? (lz[i] - lz[i + 1]) / dt : right ? (rz[i] - rz[i + 1]) / dt : 0;
      if (speed > 0.3) { phaseSpeed[i] = speed; speeds.push(speed); if (left) stanceL[i] = 1; else stanceR[i] = 1; }
    }
    speeds.sort((a, b) => a - b);
    const clipSpeed = speeds.length ? speeds[speeds.length >> 1] : 3.28;
    this.runRate = HERO.runSpeed / (clipSpeed * KAEL_SCALE);
    // 체공 구간은 평균 속도, 디딤 구간은 해당 발의 속도로 적분한다. 게임 거리를 역보간하므로 가감속에도 위상이 맞는다.
    const step = new Float32Array(n);
    for (let i = 0; i < n; i++) step[i] = (phaseSpeed[i] || clipSpeed) * dt * KAEL_SCALE;
    // 원본 보폭은 좌우 반주기 거리가 약 10% 다르다(1.862m 대 1.687m). 체공 거리만 다시 나눠 좌우 디딤 간격을 같게 한다.
    const balanced = balanceHalfCycles(step, stanceL, stanceR);
    this.runDistance = new Float32Array(n + 1);
    for (let i = 0; i < n; i++) this.runDistance[i + 1] = this.runDistance[i] + step[i];
    const r3 = (x: number) => Math.round(x * 1000) / 1000;
    this.table.push({ use: '달리기', clip: name, dur: r3(dur), clipSpeedMs: r3(clipSpeed), timeScale: r3(this.runRate), cycleDistanceM: r3(this.runDistance[n]), idleSoleM: r3(idleSole * KAEL_SCALE), runSoleM: r3(runSole * KAEL_SCALE), halfCyclesBalanced: balanced ? 1 : 0 });
  }

  // ── 재생 ────────────────────────────────────────────
  private play(key: string, clip: string, scrub: boolean, fade: number, rate = 1): THREE.AnimationAction | null {
    const next = this.actions.get(clip);
    if (!next) return this.cur;
    if (key === this.curKey) {
      if (!scrub) next.timeScale = rate;
      return next;
    }
    const prev = this.cur, prevKey = this.curKey;
    next.reset();
    next.setLoop(scrub ? THREE.LoopOnce : THREE.LoopRepeat, scrub ? 1 : Infinity);
    next.clampWhenFinished = true;
    next.timeScale = scrub ? 0 : rate;
    next.play();
    if (prev && prev !== next) {
      // 회피 출구는 직전 자세에서 연속 회전 호로 잇는다. 180도 경계에서 혼합 방향을 뒤집지 않는다.
      if (fade > 0 && this.poseTransition && (this.curKey === 'dodge' || this.poseTransition.active)) {
        prev.stop();
        this.transitionCarry = this.socketCarry;
        this.carryTransition?.reset();
        // 회피→공격은 초반 속도를 낮추고 뒤에서 합류한다. 판정 시각에는 보정이 정확히 0이다.
        this.poseTransition?.begin(fade, this.curKey === 'dodge' && key.startsWith('atk') ? 0.7 : 1);
      } else if (fade > 0) next.crossFadeFrom(prev, fade, false);
      else prev.stop();
    }
    // 대기↔달리기만 상체를 길게 잇는다. 공격·회피·스킬로 넘어가면 기존 전환(판정 자세 우선)에 맡긴다.
    const locomotionSwap = (prevKey === 'idle' && key === 'run') || (prevKey === 'run' && key === 'idle');
    if (locomotionSwap && fade > 0 && this.carryTransition && !this.poseTransition?.active) {
      this.carryFrom = this.socketCarry;
      const u = clamp01((this.gaitSpeed - 4) / 2);
      this.carryTransition.begin(CARRY_FADE_WALK + (CARRY_FADE_RUN - CARRY_FADE_WALK) * u);
    } else if (!locomotionSwap) this.carryTransition?.reset();

    this.cur = next;
    this.curKey = key;
    return next;
  }

  private scrub(key: string, clip: string, time: number, fade: number): void {
    const a = this.play(key, clip, true, fade);
    if (a) a.time = Math.max(0, Math.min(time, a.getClip().duration - 1e-4));
  }

  /** 회전 신호: 몸 방향과 실제 이동 방향의 차이(rad). 0이면 회전 디딤을 쓰지 않는다. */
  setTurnError(rad: number): void { this.turnError = Math.abs(rad); }

  update(view: GameView, alpha: number, clocks: GameClocks): void {
    if (this.curKey === 'dodge' || this.poseTransition?.active) this.poseTransition?.capture();
    // 상체 전환은 언제 시작될지 모르므로 보이던 상체를 매 프레임 저장한다(본 회전 복사만, 할당 없음).
    this.carryTransition?.capture();
    this.groundContact?.restore();
    this.poseTransition?.restore();
    this.carryTransition?.restore();
    const h = view.hero;
    const aw = view.awaken;
    const t = phaseOf(h.actTick, clocks.heroRate, alpha);
    const heroDt = clocks.heroDelta * TICK;
    const locomotion = aw.phase === AwakenPhase.None && view.flow.scene !== Scene.Result;
    const lyra = view.heroId === 'lyra';
    const p = this.root.parent;
    this.gait.update(p ? p.position.x : h.x, p ? p.position.z : h.z, heroDt,
      locomotion && h.act === HeroAct.Run, HERO.runSpeed, this.clips.get('run')?.duration ?? 1, this.runDistance);
    if (this.gait.moving) this.gaitSpeed = this.gait.speed;
    /** 칼날 폭풍의 추가 회전(rad). 다른 동작에서는 0 */
    let spin = 0;
    // 같은 동작이 다시 시작됐는지(연계 단계 변화, 틱 되감김)
    if (h.act === HeroAct.Attack) {
      if (this.lastAct !== HeroAct.Attack || h.comboStep !== this.lastStep || t < this.lastT - 0.5) this.attackSerial++;
      this.lastStep = h.comboStep;
    }
    this.lastAct = h.act;
    this.lastT = t;

    if (aw.phase === AwakenPhase.Gather || aw.phase === AwakenPhase.Flash || aw.phase === AwakenPhase.Cutin) {
      const pt = phaseOf(aw.phaseTick, 1, alpha);
      const el = aw.phase === AwakenPhase.Gather ? pt : aw.phase === AwakenPhase.Flash ? AWAKEN.gather + pt : AWAKEN.gather + AWAKEN.flash + pt;
      const p = clamp01(el / (AWAKEN.gather + AWAKEN.flash + AWAKEN.cutin));
      this.scrub('awk-up', 'powerup', this.powerup.s0 + (this.powerup.s1 - this.powerup.s0) * p, 0.08);
    } else if (aw.phase === AwakenPhase.Barrage && lyra) {
      // 라이라 폭풍 화살비: 대검 난무(combo) 대신 하늘로 활을 든 모으기 끝 자세를 유지한다(전용 몸이면 하늘 쏘기 발사 자세)
      const sky = LYRA_BOW_SEGMENTS.sky;
      if (this.clips.has(sky.clip)) this.scrub('awk-sky', sky.clip, sky.hit, 0.08); // 모으기(awk-up)와 다른 키: play()는 같은 키면 클립을 바꾸지 않는다
      else this.scrub('awk-up', 'powerup', this.powerup.s1, 0.08);
    } else if (aw.phase === AwakenPhase.Barrage) {
      const p = clamp01(phaseOf(aw.phaseTick, 1, alpha) / Math.max(1, AWAKEN.barrage));
      this.scrub('awk-bar', 'combo', this.barrage.s0 + (this.barrage.s1 - this.barrage.s0) * p, 0.05);
    } else if (view.flow.scene === Scene.Result || h.act === HeroAct.Victory) {
      this.play('victory', 'victory', false, 0.2);
    } else {
      switch (h.act) {
        case HeroAct.Run:
          if (this.gait.moving) this.scrub('run', 'run', this.gait.time, 0.1);
          else this.play('idle', 'idle', false, this.curKey === 'run' ? STOP_FADE : 0.12);
          break;
        case HeroAct.Attack: {
          if (lyra) { this.lyraScrub('latk' + this.attackSerial, lyraBasicPose(h.comboStep), t); break; }
          const g = this.attacks[h.comboStep] ?? this.attacks[0];
          // 연타 전환도 짧게 섞는다(예전 0초: 앞 타의 끝 자세에서 다음 타 시작 자세로 한 틱에 튀었다, 칼끝 p95 4.5m/틱)
          if (g) {
            const key = 'atk' + this.attackSerial;
            const recovery = g.clip === 'atk2' && this.actions.has('atk2-tail') && this.attackRecovery.select(
              this.attackSerial, this.linearTick(g, this.attackTime(h.comboStep, g, t)), this.curKey === key && this.cur === this.actions.get('atk2')
                && this.cur.getEffectiveWeight() === 1 && !this.poseTransition?.active,
            );
            // t10 경계의 같은 원본 자세에서 별도 표시 action으로 잇는다. 다른 전환 시간은 그대로 둔다.
            // 대기·달리기·회피에서 들어갈 때는 판정 틱(anchor)까지 섞는다(예전 0.06초). 판정 순간 가중치는 1이라 판정 자세는 원본 그대로다.
            this.scrub(recovery ? key + ':tail' : key, recovery ? 'atk2-tail' : g.clip, this.attackTime(h.comboStep, g, t),
              recovery ? 0 : this.curKey.startsWith('atk') ? CHAIN_FADE : Math.max(0.06, g.anchor * TICK));
          }
          break;
        }
        case HeroAct.Skill:
          if (lyra) this.lyraScrub('lsk' + h.skillId, lyraSkillPose(h.skillId, view.job?.branch === 1 ? 1 : 0), t);
          else if (h.skillId === 1) this.scrub('sk1', 'dash', segTime(this.dash, t), 0.06);
          else if (h.skillId === 2) {
            // 회전 구간을 한 번만 재생하고, 모자란 바퀴는 모델 방향을 연속으로 더 돌려 채운다.
            // 예전처럼 구간을 되감아 두 번 재생하면 되감는 순간 몸이 (416-360)° 튀고 자세도 처음으로 튀었다(칼끝 47m/틱).
            const st = this.storm;
            const p = clamp01(t / Math.max(1, SKILLS[1].duration));
            this.scrub('sk2', st.clip, st.a + (st.b - st.a) * p, 0.06);
            // 추가 회전은 360°의 배수(끝나고 0으로 돌아갈 때 방향이 튀지 않게)
            spin = st.dir * Math.round(((st.loops - 1) * st.turnDeg) / 360) * Math.PI * 2 * p;
          } else if (h.skillId === 6) {
            // 6 공중 난무: 오르기(도약) → 공중 난무(combo 구간, 5타 8~32틱) → 내려오기(도약) → 착지. 높이는 sim(h.y)
            const g = this.airSeg;
            if (t < AIR_SWING_FROM) this.scrub('sk6a', g.clip, g.s0 + (g.hit - 0.45 - g.s0) * clamp01(t / AIR_SWING_FROM), 0.06);
            else if (t <= AIR_SWING_TO && this.clips.has('combo')) {
              const p = clamp01((t - AIR_SWING_FROM) / (AIR_SWING_TO - AIR_SWING_FROM));
              this.scrub('sk6b', 'combo', this.barrage.s0 + Math.min(1.5, this.barrage.s1 - this.barrage.s0) * p, 0.08);
            } else this.scrub('sk6c', g.clip, g.hit - 0.45 + 0.45 * clamp01((t - AIR_SWING_TO) / Math.max(1, g.anchor - AIR_SWING_TO)) + (g.s1 - g.hit) * clamp01((t - g.anchor) / Math.max(1, g.total - g.anchor)), 0.08);
          } else if (h.skillId === 7) {
            // 7 대지 가르기: 낙화 착지 내려찍기 구간, SPLIT_IMPACT_TICK에 칼이 바닥
            this.scrub('sk7', this.splitSeg.clip, segTime(this.splitSeg, t), 0.06);
          } else if (h.skillId === 8) {
            // 8 혈검 회전베기: 칼날 폭풍 회전 구간 1회 + 모델 방향 8바퀴(합 9회전). 360°의 배수라 끝나고 방향이 튀지 않는다
            const st = this.storm;
            const p = clamp01(t / Math.max(1, SKILLS[7]?.duration ?? 66));
            this.scrub('sk8', st.clip, st.a + (st.b - st.a) * p, 0.06);
            spin = st.dir * SPIN_EXTRA_TURNS * Math.PI * 2 * p;
          } else if (h.skillId === 5 && this.actions.has('launcher')) {
            // 5 천공 올려베기: 전용 올려치기 클립(아래→위), 판정 틱에 칼이 가슴 높이를 지난다
            this.scrub('sk5', 'launcher', segTime(this.launcherSeg, t), 0.06);
          } else if (h.skillId === 4 || h.skillId === 5) {
            // 5 천공 올려베기: 전용 클립이 없어 초승달의 휘두름 구간을 쓴다(2026-10-22 최소 매핑).
            // 원본 2타는 후딜에 칼이 바닥 아래 0.5m까지 꽂혀 250ms 머물렀다(2026-10-27 CPU 계측). 2타 회복 표시 클립(팔만 재저작, t10 앞은 원본과 같다)을 쓴다.
            const clip = this.actions.has('atk2-tail') && this.crescent.clip === 'atk2' ? 'atk2-tail' : this.crescent.clip;
            this.scrub('sk4', clip, curveTime(h.skillId === 5 ? this.launcherCurve : this.crescentCurve, t), 0.06);
          }
          else this.scrub('sk3', 'leap', segTime(this.leap, t), 0.06);
          break;
        case HeroAct.Dodge: {
          // 50ms 혼합은 팔·골반 각속도를 증폭했다. 구르기 초반 140ms에 나눠 잇는다.
          // 4타 판정 시작 뒤와 3타 준비 중에는 기존 50ms로 둔다: 140ms를 쓰면 4타 판정 뒤 왼발가락이 최대 3.7cm 잠기고(CPU 감사),
          // 실입력 60fps 4타 판정 7틱 회피에서 피부 -0.021m, 30fps 3타 준비 3틱 회피에서 검 -0.386m가 났다(2026-10-20).
          // 클립 시간(hit) 대신 게임 공격 단계로 고른다: 실입력 4타 7틱은 판정 중이지만 표시 클립은 아직 hit 전이었다. 회피 직전 프레임 경과는 보간으로 6.x틱이라 준비 끝 1틱 전부터 짧게 둔다.
          const short = (this.lastAttackStep === 3 && this.lastAttackTick >= COMBO[3].windup - 1)
            || (this.lastAttackStep === 2 && this.lastAttackTick < COMBO[2].windup);
          this.scrub('dodge', 'dodge', segTime(this.dodge, t), short ? 0.05 : 0.14);
          break;
        }
        case HeroAct.Hurt:
          this.scrub('hurt', 'hurt', Math.min(0.55, (t * TICK) * 2.5), 0.05);
          break;
        case HeroAct.Dead:
          this.scrub('death', 'death', t * TICK, 0.1);
          break;
        default:
          // 공격이 끝나면 가드 복귀를 대기 페이드가 맡는다(공격 구간은 휘두름 정착점에서 끝난다)
          this.play('idle', 'idle', false, this.curKey.startsWith('atk') ? RECOVER_FADE : this.curKey === 'run' ? STOP_FADE : this.curKey === 'sk3' || this.curKey === 'sk1' ? LAND_FADE : 0.1);
          break;
      }
    }
    this.orient.rotation.y = (this.faceFlip ? Math.PI : 0) + spin;
    this.lastAttackStep = h.act === HeroAct.Attack ? h.comboStep : -1;
    this.lastAttackTick = t;
    const runCarry = h.act === HeroAct.Run && this.gait.moving && locomotion;
    this.mixer.update(heroDt);
    this.poseTransition?.apply(heroDt, h.act === HeroAct.Attack ? t * TICK : undefined);
    this.carryTransition?.apply(heroDt);
    // 골격과 소켓을 같은 크로스페이드 가중치로 바꾼다. 속도 역치를 넘나들어도 검만 먼저 돌아가거나 손에서 벗어나지 않는다.
    const run = this.actions.get('run');
    const atHit = h.act === HeroAct.Attack && t >= (this.attacks[h.comboStep]?.anchor ?? 0);
    const targetCarry = !atHit && run?.isScheduled() && run.enabled ? clamp01(run.getEffectiveWeight()) : 0;
    let carry = atHit ? 0 : targetCarry + (this.transitionCarry - targetCarry) * (this.poseTransition?.linearWeight ?? 0);
    // 상체 전환 중 소켓도 같은 가중치로 옮긴다(보이는 손 = 직전 손*(1-u) + 현재 손*u).
    if (!atHit && this.carryTransition?.active) carry = targetCarry + (this.carryFrom - targetCarry) * this.carryTransition.linearWeight;
    this.socketCarry = carry;
    this.swordMount.quaternion.copy(this.mountQ).slerp(this.runMountQ, carry);
    this.swordMount.position.copy(this.mountP).lerp(this.runMountP, carry);
    // 양손 쥐기: 클립의 왼손이 자루를 쥐는 동안 칼날 축을 두 손에 묶는다(달리기 한 손 휴대는 손 거리로 저절로 꺼짐)
    if (!lyra) this.applyTwoHandGrip(this.swordMount.quaternion, this.swordMount.position, heroDt);
    if (!lyra) this.applyEdgeRoll(heroDt, h.act === HeroAct.Attack || h.act === HeroAct.Skill || aw.phase !== AwakenPhase.None);
    else this.edgeHasPrev = false;
    // 스미어: 기본 공격 판정 틱 전후 칼날을 길이 방향으로 잠깐 늘인다(판정 범위·궤적 리본은 그대로, 표시 전용)
    const smearAnchor = lyra || aw.phase !== AwakenPhase.None ? -1
      : h.act === HeroAct.Attack ? this.attacks[h.comboStep]?.anchor ?? -1
      : h.act === HeroAct.Skill && (h.skillId === 4 || h.skillId === 5) ? SKILL_TUNING.crescentWindup
      : h.act === HeroAct.Skill && h.skillId === 7 ? SPLIT_IMPACT_TICK : -1;
    this.swordMesh.scale.z = 1 + SMEAR_STRETCH * (smearAnchor >= 0 ? smearWeight(t, smearAnchor) : 0);
    const grounded = locomotion && (h.act === HeroAct.Idle || h.act === HeroAct.Run);
    const groundY = grounded ? (runCarry ? this.runGroundY : this.idleGroundY) : 0;
    this.orient.position.y += (groundY - this.orient.position.y) * (1 - Math.exp(-30 * heroDt));
    if (h.act === HeroAct.Attack && t >= (this.attacks[h.comboStep]?.anchor ?? 0)) this.orient.position.y = 0;
    // 라이라 기본 공격은 제자리 사격: 대검 클립의 몸 이동을 쓰지 않는다
    this.applyRootMotion(h, aw.phase === AwakenPhase.None && !lyra ? this.attacks[h.comboStep] ?? null : null, t, heroDt);
    if (grounded && heroDt > 0) {
      // idle/run의 관절 블렌드는 두 끝 자세보다 발을 낮출 수 있다. 로딩 때 모은 발바닥 극점만 검사해 침투를 막는다.
      // 체공이면 내리지 않으며 공격·회피의 타격 궤적에는 적용하지 않는다. 프레임별 배열·벡터 생성 없음.
      this.root.updateMatrixWorld(true);
      this.mesh.skeleton.update();
      let low = Infinity;
      for (const i of this.soleSamples) {
        this.mesh.getVertexPosition(i, this.v).applyMatrix4(this.mesh.matrixWorld);
        low = Math.min(low, this.v.y);
      }
      const floor = this.root.matrixWorld.elements[13] + 0.004 * KAEL_SCALE;
      if (low < floor) this.orient.position.y += Math.min(0.06, (floor - low) / KAEL_SCALE);
    }
    // 원본 회피는 골반/허벅지까지 잠긴다. 발만 올리면 허벅지가 더 잠겨 회피 자체는 보정하지 않는다.
    const dodge = this.actions.get('dodge');
    // 회피가 섞인 공격 첫 자세는 발이 10cm 이상 잠긴다. 혼합 중 IK를 켜서 새 발 점프를 만들지 않는다.
    const dodgeBlending = dodge?.isScheduled() && dodge.enabled && dodge.getEffectiveWeight() > 1e-5;
    // 회피 중에도 발 접지를 켠다: 공격·달리기→회피 섞기에서 발가락이 바닥 아래 최대 5.7cm 잠겼다(2026-10-19). 파생 회피 클립 단독은 바닥 위라 보정이 거의 0이다.
    // 원본 회피로 되돌아간 경우(파생 설치 실패)에는 예전처럼 끈다.
    const inDodge = h.act === HeroAct.Dodge && !!this.poseTransition;
    if (locomotion && (!dodgeBlending || inDodge) && (grounded || h.act === HeroAct.Attack || inDodge)) {
      this.root.updateMatrixWorld(true);
      // 달리기 디딤 발은 수평으로 고정한다(착지 옆 미끄럼). 판정 띠 = 그 발의 달리기 발목 최저 + 0.06m(측정 정의와 같은 기준).
      const lift = this.orient.position.y;
      this.footBand[0] = (this.runAnkleMin[0] + lift) * KAEL_SCALE + 0.06;
      this.footBand[1] = (this.runAnkleMin[1] + lift) * KAEL_SCALE + 0.06;
      // 대기(멈추는 다리 섞기 포함)에도 디딘 발을 고정한다: 대기 클립의 발 흐름 0.5m/s, 멈출 때 섞기에 끌리는 발(2026-10-20 벽 정지·회전 직후 벽).
      const idlePlant = grounded && !runCarry && this.curKey === 'idle';
      this.footLock.release = this.idleAge > 0 && !idlePlant;
      this.idleAge = idlePlant ? this.idleAge + heroDt : 0;
      this.runAge = runCarry ? this.runAge + heroDt : 0;
      this.footLock.active = runCarry || idlePlant;
      this.footLock.dt = heroDt;
      // 회전 디딤: 몸이 이동 방향보다 15도 넘게 늦으면 켜기 시작해 35도에서 최대(groundContact가 끌지 않고 고정·옮겨 딛기)
      this.footLock.turn = runCarry && this.runAge >= TURN_PLANT_AFTER_RUN ? clamp01((this.turnError - TURN_PLANT_ON) / (TURN_PLANT_FULL - TURN_PLANT_ON)) : 0;
      // 대기는 끌지 않고 고정(다리 길이 한계에서만 옮김), 전환이 끝난 뒤 남은 차이는 한 발씩 느린 정리 걸음으로 맞춘다.
      this.footLock.stepAt = idlePlant || this.footLock.turn > 0 ? STEP_MAX : 0;
      this.footLock.settle = idlePlant && this.idleAge >= IDLE_SETTLE_AFTER ? IDLE_SETTLE : 0;
      this.footLock.stepTime = idlePlant ? IDLE_STEP_TIME : TURN_STEP_TIME;
      this.footLock.stepLift = idlePlant ? IDLE_STEP_LIFT : TURN_STEP_LIFT;
      this.groundContact?.apply(this.root.matrixWorld.elements[13], this.footLock);
    } else this.groundContact?.clearLocks();
  }

  /**
   * 라이라 임시 자세(c022): 사격 = 2타 휘두름 구간(몸을 비틀어 당겼다 놓음), 하늘 = 각성 모으기, 뒤로 = 피격 반동, 체공 = 도약.
   * 발사 틱(anchor)에 클립의 타격 프레임이 오게 늘이거나 줄인다. 체공은 착지(마지막 틱)에 도약 착지 프레임을 둔다
   */
  private lyraScrub(key: string, p: LyraPoseTiming, t: number): void {
    const g = this.lyraSeg;
    g.anchor = p.anchor; g.total = p.total;
    const bow = LYRA_BOW_SEGMENTS[p.kind];
    if (this.clips.has(bow.clip)) {
      // 라이라 전용 몸: 활 클립의 발사 프레임을 발사 틱에 맞춘다(체공은 착지 틱에 클립 끝)
      g.clip = bow.clip; g.s0 = bow.s0; g.hit = bow.hit; g.s1 = bow.s1;
      if (p.kind === 'air') g.anchor = Math.max(1, p.total - 1);
      this.scrub(key, g.clip, segTime(g, t), 0.06);
      return;
    }
    if (p.kind === 'air') { g.clip = this.leap.clip; g.s0 = this.leap.s0; g.hit = this.leap.hit; g.s1 = this.leap.s1; g.anchor = Math.max(1, p.total - 1); }
    else if (p.kind === 'sky') { g.clip = 'powerup'; g.s0 = this.powerup.s0; g.s1 = this.powerup.s1; g.hit = this.powerup.s0 + (this.powerup.s1 - this.powerup.s0) * 0.55; }
    else if (p.kind === 'back') { g.clip = 'hurt'; g.s0 = 0; g.hit = 0.22; g.s1 = 0.55; }
    else { const c = this.crescent; g.clip = c.clip; g.s0 = c.s0; g.hit = c.hit; g.s1 = c.s1; }
    this.scrub(key, g.clip, segTime(g, t), 0.06);
  }

  /**
   * 공격 표시 클립 시각(actionTiming 곡선). 판정 틱에는 원본 분석 hit 그대로, 끝은 칼이 바닥 위에 남는 데까지(displayS1).
   * step은 호출 호환용(곡선은 타별로 미리 만든다).
   */
  private attackTime(_step: number, g: AttackTiming, t: number): number {
    return curveTime(g.curve, t);
  }

  /** 곡선의 클립 시각을 예전 선형 펴기의 틱으로 되돌린다(2타 회복 표시 클립의 t10 경계는 클립 시각 기준으로 정해져 있다). */
  private linearTick(g: AttackTiming, clipTime: number): number {
    if (clipTime <= g.hit) return g.hit > g.s0 ? g.anchor * clamp01((clipTime - g.s0) / (g.hit - g.s0)) : g.anchor;
    return g.anchor + (g.total - g.anchor) * (g.s1 > g.hit ? clamp01((clipTime - g.hit) / (g.s1 - g.hit)) : 1);
  }

  /** 타별 표시 곡선: analyze 뒤와 후딜 표시 끝(displayS1)을 잰 뒤 다시 만든다. */
  private buildAttackCurves(): void {
    for (let k = 0; k < this.attacks.length; k++) {
      const g = this.attacks[k], d = this.displayS1[k];
      const end = d === undefined || d >= g.s1 ? g.s1 : d;
      buildAttackCurve(g.curve, g.s0, g.hit, g.sw1, end, g.anchor, g.total, SWEEP_TICKS[k] ?? 4, COIL[k] ?? 0.5);
    }
  }

  /** 로딩 때 한 번: 타별 후딜에서 검 정점이 처음 바닥 여유 아래로 내려가는 클립 시각을 찾는다(2타는 팔 회복 표시 클립이 맡아 제외). */
  private measureRecoveryClearance(): void {
    this.displayS1.length = 0;
    const sword = this.swordMount.children.find(o => (o as THREE.Mesh).isMesh) as THREE.Mesh | undefined;
    const pos = sword?.geometry.getAttribute('position');
    if (!sword || !pos) return;
    const p = new THREE.Vector3();
    for (let k = 0; k < this.attacks.length; k++) {
      const g = this.attacks[k];
      if (k === 1 && this.actions.has('atk2-tail')) continue;
      const steps = Math.max(8, Math.ceil((g.s1 - g.hit) * 240));
      for (let i = 1; i <= steps; i++) {
        const time = g.hit + (g.s1 - g.hit) * i / steps;
        this.pose(g.clip, time);
        this.swordMount.position.copy(this.mountP); this.swordMount.quaternion.copy(this.mountQ);
        this.root.updateMatrixWorld(true);
        let low = Infinity;
        for (let j = 0; j < pos.count; j++) low = Math.min(low, p.fromBufferAttribute(pos, j).applyMatrix4(sword.matrixWorld).y);
        if (low - this.root.matrixWorld.elements[13] < RECOVERY_SWORD_CLEARANCE) {
          this.displayS1[k] = g.hit + (g.s1 - g.hit) * (i - 1) / steps;
          this.table.push({ use: (k + 1) + '타 후딜 표시 끝', clip: g.clip, s1: Math.round(g.s1 * 1000) / 1000, displayS1: Math.round(this.displayS1[k] * 1000) / 1000 });
          break;
        }
      }
    }
    this.buildAttackCurves();
    this.mixer.stopAllAction();
  }

  /** 보이는 몸의 월드 중심. 그림자는 공격 root-motion XZ와 모델 높이를 함께 따른다. */
  getGroundPosition(target: THREE.Vector3): THREE.Vector3 {
    this.orient.updateWorldMatrix(true, false);
    return this.orient.getWorldPosition(target);
  }

  /**
   * 공격 중 보이는 몸 위치: 공격이 시작될 때 보이던 몸 자리에 고정하고 클립의 몸 이동만큼 옮긴다(디딘 발이 땅에 붙는다).
   * 게임 위치(부모 루트)는 COMBO.lunge만큼 따로 나아가므로 둘의 차이를 모델 오프셋으로 둔다. 공격이 끝나면 오프셋을 0으로 합친다.
   */
  private applyRootMotion(h: GameView['hero'], g: AttackTiming | null, t: number, heroDt: number): void {
    const P = this.root.parent;
    if (!P) return;
    const yaw = P.rotation.y, c = Math.cos(yaw), s = Math.sin(yaw);
    if (h.act === HeroAct.Attack && g && g.rm) {
      if (!this.rmLocked || this.rmSerial !== this.attackSerial) {
        this.rmBX = P.position.x + this.offX; this.rmBZ = P.position.z + this.offZ;
        this.rmLocked = true; this.rmSerial = this.attackSerial;
      }
      rmAt(g.rm, this.attackTime(h.comboStep, g, t), this.rmP);
      const dx = (this.rmP.x - g.rm0x) * KAEL_SCALE, dz = (this.rmP.z - g.rm0z) * KAEL_SCALE;
      let ox = this.rmBX + dx * c + dz * s - P.position.x;
      let oz = this.rmBZ - dx * s + dz * c - P.position.z;
      const len = Math.hypot(ox, oz);
      if (len > RM_MAX_OFFSET) { ox *= RM_MAX_OFFSET / len; oz *= RM_MAX_OFFSET / len; }
      this.offX = ox; this.offZ = oz;
    } else {
      this.rmLocked = false;
      const k = 1 - Math.exp(-RM_RELEASE * heroDt);
      this.offX -= this.offX * k; this.offZ -= this.offZ * k;
    }
    this.orient.position.x = (this.offX * c - this.offZ * s) / KAEL_SCALE;
    this.orient.position.z = (this.offX * s + this.offZ * c) / KAEL_SCALE;
  }

  /** 다음 update에서 자세를 처음부터 다시 고른다(장면 전환). */
  reset(): void {
    this.groundContact?.restore();
    this.groundContact?.clearLocks();
    this.poseTransition?.reset();
    this.carryTransition?.reset();
    this.mixer.stopAllAction();
    this.cur = null;
    this.socketCarry = this.transitionCarry = 0;
    this.curKey = '';
    this.lastAct = -1;
    this.attackRecovery.reset();
    this.gait.reset();
    this.rmLocked = false; this.offX = this.offZ = 0;
    this.orient.position.set(0, 0, 0);
  }

  setEmissive(hex: number, intensity: number): void {
    this.toon.emissive.setHex(hex);
    this.toon.emissiveIntensity = intensity;
  }

  /**
   * 그리기 비용 조절: 본체·외곽선·x-ray 세 번 스키닝해 그리던 것을 필요할 때만 그린다.
   * outline: 품질 단계(맨 아래 단계에서만 끈다), xray: 가림체(몹·보스·벽)가 카메라와 영웅 사이에 있을 때만.
   */
  setDrawCost(outline: boolean, xray: boolean): void {
    this.hull.visible = outline;
    this.swordHull.visible = outline;
    this.xray.visible = xray;
  }

  /** 미리 컴파일·로딩 확인용 메시 목록 */
  get drawables(): THREE.Object3D[] {
    return [this.mesh, this.hull];
  }

  dispose(): void {
    this.mixer.stopAllAction();
    this.tierCache.dispose();
    this.hullMat.dispose();
    this.xrayMat.dispose();
    this.swordMat.dispose();
    this.swordGeo.dispose();

  }
}

/** 표시 전용 복제 클립의 팔 6트랙을 time 이후 그 시각 값으로 고정한다(다리·몸통 트랙은 그대로). time에 키를 넣어 그 순간부터 정확히 멈춘다. */
function holdArmTracks(clip: THREE.AnimationClip, time: number): void {
  const q = new THREE.Quaternion(), a = new THREE.Quaternion(), b = new THREE.Quaternion();
  for (const track of clip.tracks) {
    if (!/(Left|Right)(Arm|ForeArm|Hand)\.quaternion$/.test(track.name)) continue;
    const times = track.times, values = track.values, n = times.length;
    if (n < 2 || times[n - 1] <= time) continue;
    let i = 0;
    while (i < n - 1 && times[i + 1] <= time) i++;
    const u = Math.min(1, Math.max(0, (time - times[i]) / (times[i + 1] - times[i])));
    a.fromArray(values, i * 4); b.fromArray(values, (i + 1) * 4);
    q.slerpQuaternions(a, b, u);
    const keep = times[i] < time ? i + 1 : i;
    const nextTimes = new Float32Array(keep + 2), nextValues = new Float32Array((keep + 2) * 4);
    nextTimes.set(times.subarray(0, keep)); nextValues.set(values.subarray(0, keep * 4));
    nextTimes[keep] = time; nextTimes[keep + 1] = times[n - 1];
    q.toArray(nextValues, keep * 4); q.toArray(nextValues, (keep + 1) * 4);
    track.times = nextTimes; track.values = nextValues;
  }
}
